import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import CalendarSearch from '../components/CalendarSearch';
import MarkdownNote from '../components/MarkdownNote';
import CustomerPicker from '../components/CustomerPicker';
import SocialFacePicker from '../components/SocialFacePicker';
import ShootingCompletionDialog from '../components/ShootingCompletionDialog';
import AddressInput, { AddressLink } from '../components/AddressInput';
import { toast } from 'sonner';
import {
    ChevronLeft,
    ChevronRight,
    Plus,
    Calendar as CalendarIcon,
    Clock,
    MapPin,
    RefreshCw,
    Palmtree,
    Cloud,
    CloudOff,
    Settings,
    X,
    Check,
    Trash2,
    Edit3,
    Edit,
    Users,
    User,
    Bell,
    BellRing,
    BellOff,
    AlertTriangle,
    Zap,
    Eye,
    Grid3X3,
    List,
    LayoutGrid,
    CheckCircle,
    Share2,
    CheckSquare,
    CheckCircle2,
    Globe
} from 'lucide-react';

// Gruener Haken vor dem Titel, wenn die Vorbereitung erledigt ist (ersetzt das ✅ im Titel)
const PrepMark = ({ event, className = '' }) => event?.is_prepared
    ? <CheckCircle2 className={`text-green-500 ${className}`} title="Vorbereitung erledigt" data-testid="event-prepared-mark" />
    : null;
// Gelbes Warndreieck: Shooting ist vorbei und noch nicht abgeschlossen
const DocMissingMark = ({ event, className = '' }) => event?.shooting_doc_missing
    ? <AlertTriangle className={`text-amber-500 ${className}`} title="Shooting noch nicht abgeschlossen" data-testid="event-doc-missing-mark" />
    : null;
import { 
    format, 
    startOfWeek, 
    endOfWeek, 
    startOfMonth,
    endOfMonth,
    startOfDay,
    endOfDay,
    addDays, 
    addWeeks, 
    subWeeks,
    addMonths,
    subMonths, 
    isSameDay, 
    isSameMonth,
    parseISO, 
    isWithinInterval,
    getHours,
    getMinutes,
    setHours,
    setMinutes,
    differenceInMinutes
} from 'date-fns';
import { de } from 'date-fns/locale';

// Event Templates
const EVENT_TEMPLATES = [
    { id: 'standup', name: 'Daily Standup', emoji: '☀️', duration: 15, color: '#34c759', description: 'Tägliches Team-Standup' },
    { id: 'kundencall', name: 'Kundencall', emoji: '📞', duration: 60, color: '#ff9500', description: 'Telefonat mit Kunde' },
    { id: 'meeting', name: 'Team Meeting', emoji: '👥', duration: 60, color: '#5856d6', description: 'Internes Team-Meeting' },
    { id: 'review', name: 'Code Review', emoji: '🔍', duration: 30, color: '#af52de', description: 'Code-Review Session' },
    { id: 'planning', name: 'Sprint Planning', emoji: '📋', duration: 120, color: '#ff2d55', description: 'Sprint-Planung' },
    { id: 'retro', name: 'Retrospektive', emoji: '🔄', duration: 60, color: '#00c7be', description: 'Sprint-Retrospektive' },
];

const REMINDER_OPTIONS = [
    { value: 'none', label: 'Keine' },
    { value: '5', label: '5 Minuten vorher' },
    { value: '15', label: '15 Minuten vorher' },
    { value: '30', label: '30 Minuten vorher' },
    { value: '60', label: '1 Stunde vorher' },
    { value: '120', label: '2 Stunden vorher' },
    { value: '1440', label: '1 Tag vorher' },
];

const TRAVEL_TIME_OPTIONS = [
    { value: '0', label: 'Keine Wegzeit' },
    { value: '15', label: '15 Min' },
    { value: '30', label: '30 Min' },
    { value: '45', label: '45 Min' },
    { value: '60', label: '1 Std' },
    { value: '90', label: '1.5 Std' },
    { value: '120', label: '2 Std' },
];

const RECURRENCE_OPTIONS = [
    { value: 'none', label: 'Nie' },
    { value: 'daily', label: 'Täglich' },
    { value: 'weekly', label: 'Wöchentlich' },
    { value: 'monthly', label: 'Monatlich' },
    { value: 'yearly', label: 'Jährlich' }
];

const COLORS = [
    { value: '#34c759', label: 'Grün' },
    { value: '#ff9500', label: 'Orange' },
    { value: '#ff3b30', label: 'Rot' },
    { value: '#af52de', label: 'Lila' },
    { value: '#ff2d55', label: 'Pink' },
    { value: '#5856d6', label: 'Indigo' },
    { value: '#00c7be', label: 'Türkis' },
    { value: '#8e8e93', label: 'Grau' }
];

const CalendarPage = () => {
    const { api, user } = useAuth();
    const { permission, requestPermission, scheduleEventReminders, isSupported } = useNotifications();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [viewMode, setViewMode] = useState('week'); // week, month, day
    const [events, setEvents] = useState([]);
    const [vacations, setVacations] = useState([]);
    const [users, setUsers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [showEventModal, setShowEventModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showTemplatesModal, setShowTemplatesModal] = useState(false);
    const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
    const [showConflictsModal, setShowConflictsModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    const [calendarSettings, setCalendarSettings] = useState(null);
    const [syncStatus, setSyncStatus] = useState({ connected: false, provider: null });
    const [conflicts, setConflicts] = useState([]);
    const [completionShootingId, setCompletionShootingId] = useState(null);
    const [taskDetailEvent, setTaskDetailEvent] = useState(null);
    const [draggedEvent, setDraggedEvent] = useState(null);
    
    const [eventForm, setEventForm] = useState({
        title: '',
        description: '',
        location: '',
        meeting_link: '',
        start_date: '',
        start_time: '09:00',
        end_date: '',
        end_time: '10:00',
        all_day: false,
        color: '#34c759',
        participants: [],
        recurrence: 'none',
        recurrence_end: '',
        reminder: 'none',
        travel_time: '0',
        calendar_id: '',  // For multi-calendar support
        customer_id: '',  // Optional: link to a customer (syncs to PlanFlow)
        is_prepared: false,  // Vorbereitung erledigt -> gruener Haken im Kalender
        // Shooting-Planung: legt automatisch ein SF-Shooting an
        is_shooting: false,
        shooting_lead_user_id: '',
        planned_faces: []  // [{ id, name }]
    });
    const [connectedCalendars, setConnectedCalendars] = useState([]);
    const [hiddenCalendars, setHiddenCalendars] = useState(() => {
        try { return JSON.parse(localStorage.getItem('agencyos_hidden_calendars') || '[]'); } catch { return []; }
    });
    const [showTasksInCalendar, setShowTasksInCalendar] = useState(() => {
        return localStorage.getItem('agencyos_show_tasks_calendar') === 'true';
    });
    const [taskEvents, setTaskEvents] = useState([]);

    // Calculate date ranges based on view mode
    const dateRange = useMemo(() => {
        if (viewMode === 'week') {
            return {
                start: startOfWeek(currentDate, { weekStartsOn: 1 }),
                end: endOfWeek(currentDate, { weekStartsOn: 1 })
            };
        } else if (viewMode === 'month') {
            return {
                start: startOfMonth(currentDate),
                end: endOfMonth(currentDate)
            };
        } else {
            return {
                start: startOfDay(currentDate),
                end: endOfDay(currentDate)
            };
        }
    }, [currentDate, viewMode]);

    const weekDays = useMemo(() => {
        if (viewMode === 'week') {
            return Array.from({ length: 7 }, (_, i) => addDays(dateRange.start, i));
        } else if (viewMode === 'month') {
            // Get all days in month view (including padding days)
            const monthStart = startOfMonth(currentDate);
            const monthEnd = endOfMonth(currentDate);
            const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
            const days = [];
            let day = calendarStart;
            while (day <= monthEnd || days.length % 7 !== 0) {
                days.push(day);
                day = addDays(day, 1);
                if (days.length > 42) break; // Max 6 weeks
            }
            return days;
        } else {
            return [currentDate];
        }
    }, [currentDate, viewMode, dateRange]);

    const hours = Array.from({ length: 24 }, (_, i) => i);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const promises = [
                api.get(`/calendar/v2/events?start=${dateRange.start.toISOString()}&end=${dateRange.end.toISOString()}`),
                api.get(`/calendar/v2/task-events?start=${dateRange.start.toISOString()}&end=${dateRange.end.toISOString()}`).catch(() => ({ data: [] }))
            ];

            // Only fetch static data if not already loaded
            if (connectedCalendars.length === 0) {
                promises.push(api.get('/calendar/v2/calendars'));
                promises.push(api.get('/users').catch(() => ({ data: [] })));
                promises.push(api.get('/vacations'));
                promises.push(api.get('/customers').catch(() => ({ data: [] })));
            }
            
            const results = await Promise.all(promises);
            
            setEvents(results[0].data || []);
            setTaskEvents(results[1].data || []);
            
            if (results.length > 2) {
                const v2Calendars = results[2].data || [];
                setConnectedCalendars(v2Calendars);
                setUsers(results[3].data || []);
                setVacations(results[4].data || []);
                setCustomers((results[5]?.data || []).filter(c => !c.is_archived));
                setCalendarSettings({ is_connected: true, provider: 'intern' });
                setSyncStatus({ connected: false, provider: null });
            }
            
            // Check for conflicts
            detectConflicts(results[0].data || []);
        } catch (error) {
            if (error?.name !== 'CanceledError') {
                console.error('Failed to fetch calendar data:', error);
            }
        } finally {
            setLoading(false);
        }
    }, [api, dateRange]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Deep-Link (z.B. aus Erinnerung oder altem Formular-Link): Shooting eines Termins direkt abschliessen
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const eventId = params.get('complete_event');
        if (!eventId) return;
        window.history.replaceState({}, '', window.location.pathname);
        api.get(`/calendar/events`, { params: { start: '2000-01-01', end: '2100-01-01' } }).catch(() => ({ data: [] })).then(res => {
            const ev = (res.data || []).find(e => e.id === eventId);
            if (ev?.sf_shooting_id) setCompletionShootingId(ev.sf_shooting_id);
            else toast.error('Für diesen Termin gibt es kein Shooting.');
        });
    }, [api]);

    // Conflict Detection - only for current user's own events
    const detectConflicts = (eventList) => {
        const newConflicts = [];
        // Only consider events where the current user is the owner OR is a participant
        // This excludes events created by others that the user can only view
        const userEvents = eventList.filter(e => 
            e.user_id === user?.id || 
            (e.participants && e.participants.includes(user?.id))
        );
        
        for (let i = 0; i < userEvents.length; i++) {
            for (let j = i + 1; j < userEvents.length; j++) {
                const e1 = userEvents[i];
                const e2 = userEvents[j];
                
                // Skip all-day events - they don't cause time conflicts
                if (e1.all_day || e2.all_day) continue;
                
                const start1 = parseISO(e1.start);
                const end1 = parseISO(e1.end);
                const start2 = parseISO(e2.start);
                const end2 = parseISO(e2.end);
                
                // Check overlap - only if times actually overlap
                if (start1 < end2 && start2 < end1) {
                    newConflicts.push({ event1: e1, event2: e2 });
                }
            }
        }
        setConflicts(newConflicts);
    };
    
    // Schedule notifications for events with reminders
    useEffect(() => {
        if (events.length > 0 && permission === 'granted') {
            scheduleEventReminders(events);
        }
    }, [events, permission, scheduleEventReminders]);

    // Listen for AI Assistant events to open event modal with prefilled data
    useEffect(() => {
        const handleOpenEventModal = (event) => {
            const prefill = event.detail?.prefill || {};
            const startDate = prefill.start_date || format(new Date(), 'yyyy-MM-dd');
            
            setEventForm(f => ({
                ...f,
                title: prefill.title || '',
                description: prefill.description || '',
                location: prefill.location || '',
                start_date: startDate,
                end_date: startDate,
                start_time: prefill.start_time || '09:00',
                end_time: prefill.end_time || '10:00',
                all_day: false,
                color: '#34c759',
                participants: [],
                recurrence: 'none',
                reminder: 'none'
            }));
            setSelectedEvent(null);
            setShowEventModal(true);
        };

        window.addEventListener('openEventModal', handleOpenEventModal);
        return () => window.removeEventListener('openEventModal', handleOpenEventModal);
    }, []);
    
    const handleEnableNotifications = async () => {
        const granted = await requestPermission();
        if (granted) {
            toast.success('Benachrichtigungen aktiviert!');
            scheduleEventReminders(events);
        } else {
            toast.error('Benachrichtigungen wurden abgelehnt');
        }
    };

    const handleSync = async () => {
        if (!syncStatus.connected) {
            toast.error('Kein Kalender verbunden');
            return;
        }
        setSyncing(true);
        try {
            await api.post('/calendar/sync');
            await fetchData();
            toast.success('Kalender synchronisiert!');
        } catch (error) {
            toast.error('Sync fehlgeschlagen');
        } finally {
            setSyncing(false);
        }
    };

    const handleCreateEvent = async () => {
        try {
            let startDateTime, endDateTime;
            
            if (eventForm.all_day) {
                // For all-day events, use full day range with T00:00:00 and T23:59:59
                startDateTime = `${eventForm.start_date}T00:00:00`;
                endDateTime = `${eventForm.end_date || eventForm.start_date}T23:59:59`;
            } else {
                startDateTime = `${eventForm.start_date}T${eventForm.start_time}:00`;
                endDateTime = `${eventForm.end_date || eventForm.start_date}T${eventForm.end_time}:00`;
            }

            const payload = {
                title: eventForm.title,
                description: eventForm.description,
                location: eventForm.location,
                meeting_link: eventForm.meeting_link || null,
                start: startDateTime,
                end: endDateTime,
                all_day: eventForm.all_day,
                color: eventForm.color,
                participants: eventForm.participants,
                recurrence: eventForm.recurrence !== 'none' ? eventForm.recurrence : null,
                recurrence_end: eventForm.recurrence !== 'none' && eventForm.recurrence_end ? eventForm.recurrence_end : null,
                reminder: eventForm.reminder !== 'none' ? parseInt(eventForm.reminder) : null,
                travel_time: eventForm.travel_time !== '0' ? parseInt(eventForm.travel_time) : null,
                calendar_id: eventForm.calendar_id || null,
                customer_id: eventForm.customer_id || null,
                is_prepared: !!eventForm.is_prepared,
                is_shooting: !!eventForm.is_shooting,
                shooting_lead_user_id: eventForm.is_shooting ? (eventForm.shooting_lead_user_id || null) : null,
                planned_social_face_ids: eventForm.is_shooting ? (eventForm.planned_faces || []).map(f => f.id) : []
            };
            if (payload.is_shooting && !payload.customer_id) {
                toast.error('Bei einem Shooting-Termin ist der Kunde ein Pflichtfeld.');
                return;
            }

            const eventId = selectedEvent?.recurrence_parent_id || selectedEvent?.id;

            if (selectedEvent) {
                await api.put(`/calendar/events/${eventId}`, payload);
                toast.success('Termin aktualisiert');
            } else {
                await api.post('/calendar/events', payload);
                toast.success('Termin erstellt');
            }
            
            setShowEventModal(false);
            resetEventForm();
            fetchData();
        } catch (error) {
            const msg = error?.response?.data?.detail || error?.message || 'Fehler beim Speichern';
            toast.error(msg);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm('Termin wirklich löschen?')) return;
        try {
            await api.delete(`/calendar/events/${eventId}`);
            toast.success('Termin gelöscht');
            setShowEventModal(false);
            fetchData();
        } catch (error) {
            const msg = error?.response?.data?.detail || error?.message || 'Fehler beim Löschen';
            toast.error(msg);
        }
    };

    const handleDragEnd = async (event, newDate, newHour, newMinute = 0) => {
        if (!event || !newDate) return;
        
        try {
            const oldStart = parseISO(event.start);
            const oldEnd = parseISO(event.end);
            const duration = differenceInMinutes(oldEnd, oldStart);
            
            let newStart = newDate;
            if (newHour !== undefined) {
                newStart = setHours(setMinutes(newDate, newMinute || 0), newHour);
            }
            const newEnd = new Date(newStart.getTime() + duration * 60000);
            
            await api.put(`/calendar/events/${event.recurrence_parent_id || event.id}`, {
                start: newStart.toISOString(),
                end: newEnd.toISOString()
            });
            
            toast.success(`Termin auf ${format(newStart, 'HH:mm')} verschoben`);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Verschieben');
        }
        setDraggedEvent(null);
    };

    const handleTemplateSelect = (template) => {
        const now = new Date();
        const dateStr = format(now, 'yyyy-MM-dd');
        const startHour = Math.max(9, now.getHours() + 1);
        
        setEventForm({
            title: template.name,
            description: template.description,
            location: '',
            start_date: dateStr,
            start_time: `${startHour.toString().padStart(2, '0')}:00`,
            end_date: dateStr,
            end_time: `${(startHour + Math.floor(template.duration / 60)).toString().padStart(2, '0')}:${(template.duration % 60).toString().padStart(2, '0')}`,
            all_day: false,
            color: template.color,
            participants: [],
            recurrence: 'none',
            recurrence_end: '',
            reminder: '15'
        });
        setShowTemplatesModal(false);
        setShowEventModal(true);
    };

    const resetEventForm = () => {
        // If calendars are connected, default to first one
        const defaultCalendar = connectedCalendars.length > 0 ? connectedCalendars[0] : null;
        
        setEventForm({
            title: '',
            description: '',
            location: '',
            meeting_link: '',
            start_date: '',
            start_time: '09:00',
            end_date: '',
            end_time: '10:00',
            all_day: false,
            color: defaultCalendar?.color || '#34c759',
            participants: [],
            recurrence: 'none',
            recurrence_end: '',
            reminder: 'none',
            travel_time: '0',
            calendar_id: defaultCalendar?.id || '',
            customer_id: '',
            is_prepared: false,
            is_shooting: false,
            shooting_lead_user_id: '',
            planned_faces: []
        });
        setSelectedEvent(null);
    };

    const openNewEventModal = (day = null, hour = null, minute = 0) => {
        resetEventForm();
        if (day) {
            const dateStr = format(day, 'yyyy-MM-dd');
            const startMinute = minute || 0;
            const endMinute = (startMinute + 60) % 60;
            const endHour = hour !== null ? (startMinute >= 30 ? hour + 2 : hour + 1) : 10;
            
            setEventForm(f => ({ 
                ...f, 
                start_date: dateStr, 
                end_date: dateStr,
                start_time: hour !== null 
                    ? `${hour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}` 
                    : '09:00',
                end_time: hour !== null 
                    ? `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}` 
                    : '10:00'
            }));
        }
        setSelectedDay(day);
        setShowEventModal(true);
    };

    const [showEventViewModal, setShowEventViewModal] = useState(false);
    const [viewOnlyEvent, setViewOnlyEvent] = useState(null);

    const canEditEvent = (event) => {
        if (!event) return false;
        if (event.is_task) return false;
        // Own events are always editable
        if (event.user_id === user?.id) return true;
        // System calendar events are editable by everyone
        const cal = connectedCalendars.find(c => c.id === event.calendar_id);
        if (cal?.is_system) return true;
        // Check if user is in editors list of that calendar
        if (cal?.editors?.includes(user?.id)) return true;
        // Check if user is participant
        if (event.participants?.includes(user?.id)) return true;
        return false;
    };

    const handleEventClick = (event) => {
        if (event.is_task) {
            setTaskDetailEvent(event);
            return;
        }
        // Always show the read-only details view first.
        // From there the user clicks "Bearbeiten" to enter edit mode (only if allowed).
        setViewOnlyEvent(event);
        setShowEventViewModal(true);
    };

    const openEditEventModal = (event) => {
        setSelectedEvent(event);
        const startDate = parseISO(event.start);
        const endDate = parseISO(event.end);
        
        setEventForm({
            title: event.title || '',
            description: event.description || '',
            location: event.location || '',
            meeting_link: event.meeting_link || '',
            start_date: format(startDate, 'yyyy-MM-dd'),
            start_time: event.all_day ? '09:00' : format(startDate, 'HH:mm'),
            end_date: format(endDate, 'yyyy-MM-dd'),
            end_time: event.all_day ? '10:00' : format(endDate, 'HH:mm'),
            all_day: event.all_day || false,
            color: event.color || '#34c759',
            participants: event.participants || [],
            recurrence: event.recurrence || 'none',
            recurrence_end: event.recurrence_end ? format(parseISO(event.recurrence_end), 'yyyy-MM-dd') : '',
            reminder: event.reminder ? String(event.reminder) : 'none',
            travel_time: event.travel_time ? String(event.travel_time) : '0',
            calendar_id: event.calendar_id || '',
            customer_id: event.customer_id || '',
            is_prepared: !!event.is_prepared,
            is_shooting: !!event.is_shooting,
            shooting_lead_user_id: event.shooting_lead_user_id || '',
            planned_faces: (event.planned_social_face_ids || []).map(id => ({ id, name: '' }))
        });
        setShowEventModal(true);
    };

    const getEventsForDay = (day) => {
        // Merge regular events + task events, filter out hidden calendars
        const allEvents = [...events, ...taskEvents];
        return allEvents.filter(event => {
            // Filter hidden calendars
            if (event.calendar_id && hiddenCalendars.includes(event.calendar_id)) return false;
            if (event.is_task && hiddenCalendars.includes('tasks')) return false;
            try {
                const eventStart = parseISO(event.start);
                const eventEnd = parseISO(event.end);
                return isWithinInterval(day, { start: startOfDay(eventStart), end: endOfDay(eventEnd) }) ||
                       isSameDay(eventStart, day) ||
                       isSameDay(eventEnd, day);
            } catch {
                return false;
            }
        });
    };

    const getVacationsForDay = (day) => {
        return vacations.filter(v => {
            if (v.status !== 'approved') return false;
            try {
                const start = parseISO(v.start_date);
                const end = parseISO(v.end_date);
                return isWithinInterval(day, { start: startOfDay(start), end: endOfDay(end) });
            } catch {
                return false;
            }
        });
    };

    const getVacationSpan = (vacation, day) => {
        const start = parseISO(vacation.start_date);
        const end = parseISO(vacation.end_date);
        const isStart = isSameDay(start, day);
        const isEnd = isSameDay(end, day);
        const isMiddle = !isStart && !isEnd;
        return { isStart, isEnd, isMiddle };
    };

    const isToday = (day) => isSameDay(day, new Date());

    const toggleCalendarVisibility = (calId) => {
        setHiddenCalendars(prev => {
            const next = prev.includes(calId) ? prev.filter(id => id !== calId) : [...prev, calId];
            localStorage.setItem('agencyos_hidden_calendars', JSON.stringify(next));
            return next;
        });
    };

    const navigate = (direction) => {
        if (viewMode === 'week') {
            setCurrentDate(direction > 0 ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
        } else if (viewMode === 'month') {
            setCurrentDate(direction > 0 ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
        } else {
            setCurrentDate(direction > 0 ? addDays(currentDate, 1) : addDays(currentDate, -1));
        }
    };

    // Get event position for day view
    const getEventStyle = (event) => {
        try {
            const start = parseISO(event.start);
            const end = parseISO(event.end);
            const startMinutes = getHours(start) * 60 + getMinutes(start);
            const endMinutes = getHours(end) * 60 + getMinutes(end);
            const top = (startMinutes / 60) * 60; // 60px per hour
            const height = Math.max(((endMinutes - startMinutes) / 60) * 60, 20);
            return { top: `${top}px`, height: `${height}px` };
        } catch {
            return { top: '0px', height: '60px' };
        }
    };

    return (
        <div className="flex-1 flex flex-col h-full overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 dark:from-background dark:to-card">
            {/* Header */}
            <div className="flex-shrink-0 px-4 md:px-6 py-4 bg-white/80 dark:bg-card/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-border">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg shadow-primary/20">
                            <CalendarIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Kalender</h1>
                            <p className="text-sm text-gray-500 dark:text-muted-foreground">
                                {viewMode === 'week' && `${format(dateRange.start, 'd. MMM', { locale: de })} - ${format(dateRange.end, 'd. MMM yyyy', { locale: de })}`}
                                {viewMode === 'month' && format(currentDate, 'MMMM yyyy', { locale: de })}
                                {viewMode === 'day' && format(currentDate, 'EEEE, d. MMMM yyyy', { locale: de })}
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        {/* Conflict Warning - clickable */}
                        {conflicts.length > 0 && (
                            <Badge 
                                variant="destructive" 
                                className="hidden sm:flex items-center gap-1 cursor-pointer hover:bg-red-600 transition-colors"
                                onClick={() => setShowConflictsModal(true)}
                            >
                                <AlertTriangle className="w-3 h-3" />
                                {conflicts.length} Konflikt{conflicts.length > 1 ? 'e' : ''}
                            </Badge>
                        )}
                        
                        {/* Calendar count */}
                        <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-sm bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            <CalendarIcon className="w-4 h-4" />
                            <span>{connectedCalendars.length} Kalender</span>
                        </div>
                        
                        <Button variant="outline" size="sm" onClick={() => setShowAvailabilityModal(true)} className="rounded-xl hidden sm:flex">
                            <Eye className="w-4 h-4 mr-2" />
                            Verfügbarkeit
                        </Button>
                        
                        <Button variant="outline" size="sm" onClick={() => setShowTemplatesModal(true)} className="rounded-xl">
                            <Zap className="w-4 h-4" />
                        </Button>
                        
                        {/* Notification Toggle Button */}
                        {isSupported && (
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={handleEnableNotifications}
                                className={`rounded-xl ${permission === 'granted' ? 'text-green-600 border-green-300' : ''}`}
                                title={permission === 'granted' ? 'Benachrichtigungen aktiv' : 'Benachrichtigungen aktivieren'}
                            >
                                {permission === 'granted' ? (
                                    <BellRing className="w-4 h-4" />
                                ) : permission === 'denied' ? (
                                    <BellOff className="w-4 h-4" />
                                ) : (
                                    <Bell className="w-4 h-4" />
                                )}
                            </Button>
                        )}
                        
                        <Button variant="outline" size="sm" onClick={() => setShowSettingsModal(true)} className="rounded-xl" data-testid="calendar-settings-btn">
                            <Settings className="w-4 h-4" />
                        </Button>
                        
                        <Button onClick={() => openNewEventModal()} className="rounded-xl bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/20">
                            <Plus className="w-4 h-4 mr-2" />
                            <span className="hidden sm:inline">Neu</span>
                        </Button>
                    </div>
                </div>
                
                {/* Navigation & View Toggle */}
                <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="rounded-xl">
                            <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date())} className="rounded-xl px-4">
                            Heute
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => navigate(1)} className="rounded-xl">
                            <ChevronRight className="w-5 h-5" />
                        </Button>

                        {/* Calendar Filters - inline */}
                        {connectedCalendars.length > 0 && (
                            <div className="hidden sm:flex items-center gap-1.5 ml-3 pl-3 border-l border-border" data-testid="calendar-filter-strip">
                                {connectedCalendars.map(cal => (
                                    <button
                                        key={cal.id}
                                        onClick={() => toggleCalendarVisibility(cal.id)}
                                        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                                            hiddenCalendars.includes(cal.id) ? 'opacity-35' : ''
                                        }`}
                                        data-testid={`calendar-toggle-${cal.id}`}
                                    >
                                        <div 
                                            className="w-2.5 h-2.5 rounded-sm flex-shrink-0 flex items-center justify-center"
                                            style={{ backgroundColor: hiddenCalendars.includes(cal.id) ? 'transparent' : cal.color, border: `2px solid ${cal.color}` }}
                                        >
                                            {!hiddenCalendars.includes(cal.id) && (
                                                <Check className="w-1.5 h-1.5 text-white" />
                                            )}
                                        </div>
                                        <span>{cal.name}</span>
                                    </button>
                                ))}
                                {showTasksInCalendar && (
                                    <button
                                        onClick={() => toggleCalendarVisibility('tasks')}
                                        className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-all ${
                                            hiddenCalendars.includes('tasks') ? 'opacity-35' : ''
                                        }`}
                                        data-testid="calendar-toggle-tasks"
                                    >
                                        <div
                                            className="w-2.5 h-2.5 rounded-sm flex-shrink-0 flex items-center justify-center"
                                            style={{ backgroundColor: hiddenCalendars.includes('tasks') ? 'transparent' : '#5856d6', border: '2px solid #5856d6' }}
                                        >
                                            {!hiddenCalendars.includes('tasks') && (
                                                <Check className="w-1.5 h-1.5 text-white" />
                                            )}
                                        </div>
                                        <span>Tasks</span>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    
                    {/* View Mode Toggle */}
                    <div className="flex items-center bg-gray-100 dark:bg-secondary rounded-xl p-1">
                        <Button 
                            variant={viewMode === 'day' ? 'default' : 'ghost'} 
                            size="sm" 
                            onClick={() => setViewMode('day')}
                            className="rounded-lg"
                        >
                            <List className="w-4 h-4 mr-1" />
                            Tag
                        </Button>
                        <Button 
                            variant={viewMode === 'week' ? 'default' : 'ghost'} 
                            size="sm" 
                            onClick={() => setViewMode('week')}
                            className="rounded-lg"
                        >
                            <Grid3X3 className="w-4 h-4 mr-1" />
                            Woche
                        </Button>
                        <Button 
                            variant={viewMode === 'month' ? 'default' : 'ghost'} 
                            size="sm" 
                            onClick={() => setViewMode('month')}
                            className="rounded-lg"
                        >
                            <LayoutGrid className="w-4 h-4 mr-1" />
                            Monat
                        </Button>
                    </div>
                    
                    {/* Month/Year Selector */}
                    <div className="hidden sm:flex items-center gap-2">
                        <Select value={format(currentDate, 'M')} onValueChange={(month) => {
                            const newDate = new Date(currentDate);
                            newDate.setMonth(parseInt(month) - 1);
                            setCurrentDate(newDate);
                        }}>
                            <SelectTrigger className="w-[130px] rounded-xl">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Array.from({ length: 12 }, (_, i) => (
                                    <SelectItem key={i} value={String(i + 1)}>
                                        {format(new Date(2024, i, 1), 'MMMM', { locale: de })}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        
                        <Select value={format(currentDate, 'yyyy')} onValueChange={(year) => {
                            const newDate = new Date(currentDate);
                            newDate.setFullYear(parseInt(year));
                            setCurrentDate(newDate);
                        }}>
                            <SelectTrigger className="w-[90px] rounded-xl">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Array.from({ length: 10 }, (_, i) => {
                                    const year = new Date().getFullYear() - 2 + i;
                                    return <SelectItem key={year} value={String(year)}>{year}</SelectItem>;
                                })}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Calendar Views */}
            <div className="flex-1 overflow-auto p-2 md:p-4">
                {/* Quick Search */}
                <CalendarSearch
                    events={events}
                    taskEvents={taskEvents}
                    vacations={vacations}
                    users={users}
                    setCurrentDate={setCurrentDate}
                    setViewMode={setViewMode}
                    openEditEventModal={handleEventClick}
                />
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                    </div>
                ) : (
                    <>
                        {/* Empty state hint - if user has no visible calendars or no events anywhere */}
                        {events.length === 0 && taskEvents.length === 0 && (() => {
                            const visibleCalendars = connectedCalendars.filter(c => c.visible !== false);
                            const noVisibleCals = connectedCalendars.length > 0 && visibleCalendars.length === 0;
                            if (!noVisibleCals) return null;
                            return (
                                <div className="mb-4 mx-4 rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 flex items-start gap-3" data-testid="calendar-empty-hint">
                                    <div className="text-2xl shrink-0">👀</div>
                                    <div className="flex-1 text-sm">
                                        <p className="font-semibold text-amber-900 dark:text-amber-200 mb-1">
                                            Du hast alle Kalender ausgeblendet
                                        </p>
                                        <p className="text-amber-900/80 dark:text-amber-200/80 mb-2">
                                            Klicke oben auf "Kalender" und aktiviere mindestens einen, um Termine zu sehen.
                                        </p>
                                    </div>
                                </div>
                            );
                        })()}

                        {viewMode === 'week' && (
                            <WeekView 
                                weekDays={weekDays}
                                events={events}
                                vacations={vacations}
                                users={users}
                                isToday={isToday}
                                getEventsForDay={getEventsForDay}
                                getVacationsForDay={getVacationsForDay}
                                getVacationSpan={getVacationSpan}
                                openNewEventModal={openNewEventModal}
                                openEditEventModal={handleEventClick}
                                onDragEnd={handleDragEnd}
                                draggedEvent={draggedEvent}
                                setDraggedEvent={setDraggedEvent}
                                connectedCalendars={connectedCalendars}
                            />
                        )}
                        
                        {viewMode === 'month' && (
                            <MonthView 
                                days={weekDays}
                                currentDate={currentDate}
                                events={events}
                                isToday={isToday}
                                getEventsForDay={getEventsForDay}
                                openNewEventModal={openNewEventModal}
                                openEditEventModal={handleEventClick}
                            />
                        )}
                        
                        {viewMode === 'day' && (
                            <DayView 
                                currentDate={currentDate}
                                events={getEventsForDay(currentDate)}
                                vacations={getVacationsForDay(currentDate)}
                                hours={hours}
                                openNewEventModal={openNewEventModal}
                                openEditEventModal={handleEventClick}
                                onDragEnd={handleDragEnd}
                            />
                        )}
                    </>
                )}
            </div>

            {/* Event Modal */}
            <EventModal 
                open={showEventModal}
                onOpenChange={setShowEventModal}
                eventForm={eventForm}
                setEventForm={setEventForm}
                selectedEvent={selectedEvent}
                users={users}
                connectedCalendars={connectedCalendars}
                customers={customers}
                onSave={handleCreateEvent}
                onDelete={handleDeleteEvent}
                onCompleteShooting={(ev) => { setShowEventModal(false); setCompletionShootingId(ev.sf_shooting_id); }}
            />

            {/* Shooting abschliessen: Zeiten, Teilnehmer, Saetze -> direkt zur Abrechnung freigegeben */}
            <ShootingCompletionDialog
                open={!!completionShootingId}
                shootingId={completionShootingId}
                onOpenChange={(v) => { if (!v) setCompletionShootingId(null); }}
                onCompleted={() => fetchData()}
            />

            {/* Templates Modal */}
            <TemplatesModal 
                open={showTemplatesModal}
                onOpenChange={setShowTemplatesModal}
                onSelect={handleTemplateSelect}
            />

            {/* Availability Modal */}
            <AvailabilityModal 
                open={showAvailabilityModal}
                onOpenChange={setShowAvailabilityModal}
                users={users}
                events={events}
                currentDate={currentDate}
            />

            {/* Conflicts Modal */}
            <ConflictsModal
                open={showConflictsModal}
                onOpenChange={setShowConflictsModal}
                conflicts={conflicts}
                onEditEvent={(event) => {
                    setShowConflictsModal(false);
                    setSelectedEvent(event);
                    setEventForm({
                        title: event.title || '',
                        description: event.description || '',
                        location: event.location || '',
                        all_day: event.all_day || false,
                        start: event.start ? event.start.substring(0, 16) : '',
                        end: event.end ? event.end.substring(0, 16) : '',
                        color: event.color || '#3B82F6',
                        reminder: event.reminder || 'none',
                        recurrence: event.recurrence || 'none',
                        participants: event.participants || [],
                        target_calendar: event.calendar_id || ''
                    });
                    setShowEventModal(true);
                }}
                onDeleteEvent={async (eventId) => {
                    try {
                        await api.delete(`/calendar/events/${eventId}`);
                        toast.success('Event gelöscht');
                        fetchData();
                    } catch (err) {
                        toast.error('Fehler beim Löschen');
                    }
                }}
            />

            {/* Settings Modal */}
            <CalendarSettingsModal 
                open={showSettingsModal}
                onOpenChange={setShowSettingsModal}
                onSave={() => { fetchData(); setShowSettingsModal(false); }}
                showTasksInCalendar={showTasksInCalendar}
                setShowTasksInCalendar={setShowTasksInCalendar}
            />

            {/* Task Detail Modal */}
            <Dialog open={!!taskDetailEvent} onOpenChange={(v) => !v && setTaskDetailEvent(null)}>
                <DialogContent className="max-w-md p-4 sm:p-6">
                    <DialogHeader className="pb-2">
                        <DialogTitle className="flex items-center gap-2 text-base">
                            <CheckSquare className="w-4 h-4" style={{ color: taskDetailEvent?.color }} />
                            Task Details
                        </DialogTitle>
                    </DialogHeader>
                    {taskDetailEvent && (
                        <div className="space-y-3">
                            <div className="p-3 rounded-xl border-2 border-dashed" style={{ borderColor: taskDetailEvent.color, backgroundColor: `${taskDetailEvent.color}10` }}>
                                <h3 className="font-bold text-lg" style={{ color: taskDetailEvent.color }}>{taskDetailEvent.title}</h3>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-2.5 rounded-lg bg-muted/50">
                                    <p className="text-[10px] text-muted-foreground uppercase">Deadline</p>
                                    <p className="font-medium text-sm">{taskDetailEvent.start ? format(parseISO(taskDetailEvent.start), 'dd.MM.yyyy') : '-'}</p>
                                </div>
                                <div className="p-2.5 rounded-lg bg-muted/50">
                                    <p className="text-[10px] text-muted-foreground uppercase">Status</p>
                                    <p className="font-medium text-sm capitalize">{taskDetailEvent.task_status || '-'}</p>
                                </div>
                            </div>

                            {taskDetailEvent.task_priority !== undefined && (
                                <div className="p-2.5 rounded-lg bg-muted/50">
                                    <p className="text-[10px] text-muted-foreground uppercase">Prioritaet</p>
                                    <p className="font-medium text-sm">
                                        {taskDetailEvent.task_priority === 1 ? 'Hoch' : taskDetailEvent.task_priority === 2 ? 'Mittel' : 'Niedrig'}
                                    </p>
                                </div>
                            )}

                            {taskDetailEvent.task_description && (
                                <div className="p-2.5 rounded-lg bg-muted/50">
                                    <p className="text-[10px] text-muted-foreground uppercase">Beschreibung</p>
                                    <p className="text-sm mt-1 whitespace-pre-wrap">{taskDetailEvent.task_description}</p>
                                </div>
                            )}

                            {taskDetailEvent.task_assigned_names?.length > 0 && (
                                <div className="p-2.5 rounded-lg bg-muted/50">
                                    <p className="text-[10px] text-muted-foreground uppercase">Zugewiesen an</p>
                                    <div className="flex flex-wrap gap-1.5 mt-1">
                                        {taskDetailEvent.task_assigned_names.map((name, i) => (
                                            <span key={i} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">{name}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => { setTaskDetailEvent(null); window.location.href = '/tasks'; }}
                            >
                                Zum Task wechseln
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* View-Only Event Modal (subscribed calendars without edit permission) */}
            <Dialog open={showEventViewModal} onOpenChange={setShowEventViewModal}>
                <DialogContent className="max-w-md p-4 sm:p-6 max-h-[90vh] flex flex-col gap-0">
                    <DialogHeader className="pb-2 flex-shrink-0">
                        <DialogTitle className="flex items-center gap-2 text-base">
                            <Eye className="w-4 h-4 text-muted-foreground" />
                            Termin-Info
                        </DialogTitle>
                    </DialogHeader>
                    {viewOnlyEvent && (
                        <div className="space-y-3 overflow-y-auto flex-1 -mx-4 px-4 sm:-mx-6 sm:px-6 pb-2" data-testid="event-view-scroll">
                            <div className="p-3 rounded-xl" style={{ backgroundColor: `${viewOnlyEvent.color || '#34c759'}20`, borderLeft: `4px solid ${viewOnlyEvent.color || '#34c759'}` }}>
                                <h3 className="font-bold text-lg">{viewOnlyEvent.title}</h3>
                                {viewOnlyEvent.created_by_name && (
                                    <p className="text-sm text-muted-foreground mt-0.5">von {viewOnlyEvent.created_by_name}</p>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-2.5 rounded-lg bg-muted/50">
                                    <p className="text-[10px] text-muted-foreground uppercase">Datum</p>
                                    <p className="font-medium text-sm">{viewOnlyEvent.start ? format(parseISO(viewOnlyEvent.start), 'dd.MM.yyyy') : '-'}</p>
                                </div>
                                <div className="p-2.5 rounded-lg bg-muted/50">
                                    <p className="text-[10px] text-muted-foreground uppercase">Uhrzeit</p>
                                    <p className="font-medium text-sm">
                                        {viewOnlyEvent.all_day ? 'Ganztaegig' : `${viewOnlyEvent.start ? format(parseISO(viewOnlyEvent.start), 'HH:mm') : ''} - ${viewOnlyEvent.end ? format(parseISO(viewOnlyEvent.end), 'HH:mm') : ''}`}
                                    </p>
                                </div>
                            </div>

                            {viewOnlyEvent.location && (
                                <div className="p-2.5 rounded-lg bg-muted/50 flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-[10px] text-muted-foreground uppercase">Ort</p>
                                        <AddressLink address={viewOnlyEvent.location} className="text-sm text-primary" iconClassName="w-3.5 h-3.5" />
                                    </div>
                                </div>
                            )}

                            {viewOnlyEvent.meeting_link && (
                                <a href={viewOnlyEvent.meeting_link} target="_blank" rel="noopener noreferrer"
                                    className="p-2.5 rounded-lg bg-blue-500/10 flex items-center gap-2 hover:bg-blue-500/20 transition-colors block">
                                    <Globe className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                    <div>
                                        <p className="text-[10px] text-blue-500 uppercase">Online-Meeting</p>
                                        <p className="text-sm text-blue-600 truncate">{viewOnlyEvent.meeting_link}</p>
                                    </div>
                                </a>
                            )}

                            {viewOnlyEvent.description && (
                                <div className="p-2.5 rounded-lg bg-muted/50">
                                    <p className="text-[10px] text-muted-foreground uppercase">Notizen</p>
                                    <MarkdownNote text={viewOnlyEvent.description} className="mt-1" />
                                </div>
                            )}

                            {viewOnlyEvent.participants && viewOnlyEvent.participants.length > 0 && (
                                <div className="p-2.5 rounded-lg bg-muted/50">
                                    <p className="text-[10px] text-muted-foreground uppercase mb-1.5">Teilnehmer</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {viewOnlyEvent.participants.map((pid) => {
                                            const u = users.find(x => x.id === pid);
                                            return (
                                                <span key={pid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background text-xs">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                                                    {u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : 'Teilnehmer'}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {(viewOnlyEvent.calendar_name || viewOnlyEvent.calendar_id) && (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: viewOnlyEvent.color || '#34c759' }} />
                                    <span>Kalender: {viewOnlyEvent.calendar_name || connectedCalendars.find(c => c.id === viewOnlyEvent.calendar_id)?.name || 'Standard'}</span>
                                </div>
                            )}

                            {/* Shooting: Abschluss direkt aus dem Termin-Info-Fenster */}
                            {viewOnlyEvent.is_shooting && (
                                <div className={`p-3 rounded-xl border ${viewOnlyEvent.shooting_doc_missing ? 'border-amber-500/50 bg-amber-500/10' : viewOnlyEvent.shooting_doc_id ? 'border-green-500/40 bg-green-500/10' : 'border-primary/30 bg-primary/5'}`} data-testid="event-view-shooting">
                                    <p className="text-[10px] text-muted-foreground uppercase mb-1">Shooting</p>
                                    {viewOnlyEvent.shooting_doc_id ? (
                                        <p className="text-sm text-green-600 dark:text-green-400 font-medium">✓ Abgeschlossen und zur Abrechnung freigegeben</p>
                                    ) : viewOnlyEvent.shooting_doc_missing ? (
                                        <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">⚠ Vorbei, aber noch nicht abgeschlossen</p>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">Nach dem Shooting hier abschließen – Zeiten je Person, Stundensätze, Team, Notiz.</p>
                                    )}
                                    {viewOnlyEvent.sf_shooting_id ? (
                                        <Button
                                            className="w-full mt-2"
                                            size="sm"
                                            variant={viewOnlyEvent.shooting_doc_id ? 'outline' : 'default'}
                                            onClick={() => { setShowEventViewModal(false); setCompletionShootingId(viewOnlyEvent.sf_shooting_id); }}
                                            data-testid="event-view-complete-shooting"
                                        >
                                            {viewOnlyEvent.shooting_doc_id ? 'Abschluss ansehen / bearbeiten' : 'Shooting abschließen'}
                                        </Button>
                                    ) : (
                                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Für diesen Termin fehlt noch der Abrechnungs-Datensatz – bitte den Termin einmal über „Bearbeiten“ speichern.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {viewOnlyEvent && (
                        <div className="flex gap-2 pt-3 mt-2 border-t border-border flex-shrink-0">
                            <Button variant="outline" className="flex-1" onClick={() => setShowEventViewModal(false)} data-testid="event-view-close">
                                Schließen
                            </Button>
                            {canEditEvent(viewOnlyEvent) ? (
                                <Button
                                    className="flex-1"
                                    onClick={() => {
                                        setShowEventViewModal(false);
                                        // Slight delay to avoid double-modal animations
                                        setTimeout(() => openEditEventModal(viewOnlyEvent), 50);
                                    }}
                                    data-testid="event-view-edit"
                                >
                                    <Edit className="w-4 h-4 mr-1.5" /> Bearbeiten
                                </Button>
                            ) : (
                                <p className="flex-1 text-xs text-center text-muted-foreground italic pt-2">
                                    Keine Berechtigung zum Bearbeiten
                                </p>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Week View Component with Hour Grid
const WeekView = ({ weekDays, events, vacations, users, isToday, getEventsForDay, getVacationsForDay, getVacationSpan, openNewEventModal, openEditEventModal, onDragEnd, draggedEvent, setDraggedEvent, connectedCalendars = [] }) => {
    const [dragOverSlot, setDragOverSlot] = useState(null);
    // Initialize with the first day of the current week or today if it's in the week
    const [mobileSelectedDay, setMobileSelectedDay] = useState(() => {
        const today = new Date();
        // Check if today is in weekDays
        if (weekDays && weekDays.length > 0) {
            const todayInWeek = weekDays.find(d => isSameDay(d, today));
            return todayInWeek || weekDays[0];
        }
        return today;
    });
    
    // Update mobileSelectedDay when weekDays changes (e.g., navigating weeks)
    useEffect(() => {
        if (weekDays && weekDays.length > 0) {
            // If current selected day is not in the new week, select first day
            const isInWeek = weekDays.some(d => isSameDay(d, mobileSelectedDay));
            if (!isInWeek) {
                // Try to select today if it's in the new week, otherwise first day
                const today = new Date();
                const todayInWeek = weekDays.find(d => isSameDay(d, today));
                setMobileSelectedDay(todayInWeek || weekDays[0]);
            }
        }
    }, [weekDays]);
    
    // Hours from 0am to 24pm for week view (full day)
    const hours = Array.from({ length: 24 }, (_, i) => i);
    
    // Check if mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    
    // Group vacations into rows
    const getVacationRows = () => {
        const approvedVacations = vacations.filter(v => v.status === 'approved');
        const rows = [];
        
        approvedVacations.forEach(vacation => {
            const overlapsWeek = weekDays.some(day => {
                try {
                    const start = parseISO(vacation.start_date);
                    const end = parseISO(vacation.end_date);
                    return isWithinInterval(day, { start: startOfDay(start), end: endOfDay(end) });
                } catch { return false; }
            });
            
            if (overlapsWeek) {
                let rowIndex = rows.findIndex(row => 
                    !row.some(v => {
                        try {
                            const rStart = parseISO(v.start_date);
                            const rEnd = parseISO(v.end_date);
                            const vStart = parseISO(vacation.start_date);
                            const vEnd = parseISO(vacation.end_date);
                            return !(vEnd < rStart || vStart > rEnd);
                        } catch { return false; }
                    })
                );
                
                if (rowIndex === -1) {
                    rows.push([vacation]);
                } else {
                    rows[rowIndex].push(vacation);
                }
            }
        });
        
        return rows;
    };
    
    const vacationRows = getVacationRows();
    
    // Get event position and height based on time
    const getEventStyle = (event) => {
        try {
            const start = parseISO(event.start);
            const end = parseISO(event.end);
            const startHour = getHours(start);
            const startMinute = getMinutes(start);
            const endHour = getHours(end);
            const endMinute = getMinutes(end);
            
            // Calculate position (50px per hour, starting from 0)
            const top = startHour * 50 + (startMinute / 60) * 50;
            const duration = (endHour - startHour) * 50 + ((endMinute - startMinute) / 60) * 50;
            
            return {
                top: `${top}px`,
                height: `${Math.max(duration, 20)}px`,
            };
        } catch {
            return { top: '0px', height: '50px' };
        }
    };

    /**
     * Column-packing layout (Google/Apple Calendar style).
     * Overlapping events are placed side-by-side in columns within their cluster.
     * Each event gets {leftPct, widthPct} so all are fully visible.
     */
    const computeOverlapLayout = (timedEvents) => {
        const layout = {};
        if (!timedEvents || timedEvents.length === 0) return layout;
        const items = timedEvents.map(e => {
            try {
                return { id: e.id, start: parseISO(e.start).getTime(), end: parseISO(e.end).getTime() };
            } catch {
                return { id: e.id, start: 0, end: 0 };
            }
        }).sort((a, b) => a.start - b.start || a.end - b.end);

        // Build clusters of mutually overlapping events
        let cluster = [];
        let clusterEnd = -Infinity;
        const clusters = [];
        items.forEach(it => {
            if (it.start >= clusterEnd) {
                if (cluster.length) clusters.push(cluster);
                cluster = [it];
                clusterEnd = it.end;
            } else {
                cluster.push(it);
                if (it.end > clusterEnd) clusterEnd = it.end;
            }
        });
        if (cluster.length) clusters.push(cluster);

        clusters.forEach(group => {
            // Assign each event to the lowest free column
            const columnsEnd = []; // each entry holds the end-timestamp of last event in that column
            const groupAssignments = {};
            group.forEach(it => {
                let placed = false;
                for (let c = 0; c < columnsEnd.length; c++) {
                    if (columnsEnd[c] <= it.start) {
                        columnsEnd[c] = it.end;
                        groupAssignments[it.id] = c;
                        placed = true;
                        break;
                    }
                }
                if (!placed) {
                    columnsEnd.push(it.end);
                    groupAssignments[it.id] = columnsEnd.length - 1;
                }
            });
            const total = columnsEnd.length;
            group.forEach(it => {
                const col = groupAssignments[it.id];
                layout[it.id] = {
                    leftPct: (col / total) * 100,
                    widthPct: (1 / total) * 100,
                    total,
                    col,
                };
            });
        });
        return layout;
    };
    
    const handleDrop = (e, day, hour, minute) => {
        e.preventDefault();
        setDragOverSlot(null);
        if (draggedEvent) {
            onDragEnd(draggedEvent, day, hour, minute);
        }
    };
    
    // Mobile View: Show one day at a time with horizontal day selector
    const MobileDayView = () => {
        const selectedDayEvents = getEventsForDay(mobileSelectedDay).filter(e => !e.all_day);
        const allDayEvents = getEventsForDay(mobileSelectedDay).filter(e => e.all_day);
        const dayVacations = getVacationsForDay(mobileSelectedDay);
        
        return (
            <div className="bg-white dark:bg-card rounded-2xl shadow-lg overflow-hidden border border-gray-200/50 dark:border-border">
                {/* Horizontal Day Selector */}
                <div className="flex overflow-x-auto gap-1 p-3 bg-gray-50 dark:bg-secondary/50 border-b border-gray-200 dark:border-border scrollbar-hide">
                    {weekDays.map((day, index) => (
                        <button
                            key={index}
                            onClick={() => setMobileSelectedDay(day)}
                            className={`flex-shrink-0 px-4 py-2 rounded-xl text-center transition-all ${
                                isSameDay(day, mobileSelectedDay)
                                    ? 'bg-primary text-white shadow-lg'
                                    : isToday(day)
                                    ? 'bg-primary/20 text-primary'
                                    : 'bg-white dark:bg-secondary hover:bg-gray-100 dark:hover:bg-secondary'
                            }`}
                        >
                            <p className="text-xs font-medium opacity-80">
                                {format(day, 'EEE', { locale: de })}
                            </p>
                            <p className="text-lg font-semibold">
                                {format(day, 'd')}
                            </p>
                        </button>
                    ))}
                </div>
                
                {/* Selected Day Header */}
                <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-card dark:to-card/50 border-b border-gray-200 dark:border-border">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {format(mobileSelectedDay, 'EEEE, d. MMMM', { locale: de })}
                    </h3>
                    <p className="text-sm text-gray-500">
                        {selectedDayEvents.length + allDayEvents.length} Termine
                    </p>
                </div>

                {/* Vacations for selected day */}
                {dayVacations.length > 0 && (
                    <div className="p-3 space-y-2 border-b border-gray-200 dark:border-border bg-amber-50/50 dark:bg-amber-900/10">
                        {dayVacations.map(vacation => {
                            const user = users.find(u => u.id === vacation.user_id);
                            return (
                                <div 
                                    key={vacation.id}
                                    className="flex items-center gap-3 p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl"
                                >
                                    <Palmtree className="w-5 h-5 text-amber-600" />
                                    <div className="flex-1">
                                        <p className="font-medium text-amber-800 dark:text-amber-200">
                                            {user?.first_name} {user?.last_name} - Urlaub
                                        </p>
                                        <p className="text-xs text-amber-600 dark:text-amber-400">
                                            {format(parseISO(vacation.start_date), 'd. MMM', { locale: de })} - {format(parseISO(vacation.end_date), 'd. MMM', { locale: de })}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* All-day Events + Task Events */}
                {allDayEvents.length > 0 && (
                    <div className="p-3 space-y-2 border-b border-gray-200 dark:border-border bg-blue-50/50 dark:bg-primary/10">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide px-1">Ganztaegig / Tasks</p>
                        {allDayEvents.map(event => event.is_task ? (
                            <div
                                key={event.id}
                                onClick={() => setTaskDetailEvent(event)}
                                className="p-3 rounded-xl cursor-pointer hover:scale-[1.01] transition-all border-2 border-dashed"
                                style={{ borderColor: event.color || '#5856D6', backgroundColor: `${event.color || '#5856D6'}15` }}
                                data-testid={`task-event-${event.task_id}`}
                            >
                                <div className="flex items-center gap-2">
                                    <CheckSquare className="w-4 h-4 flex-shrink-0" style={{ color: event.color }} />
                                    <p className="font-semibold text-sm" style={{ color: event.color }}>{event.title}</p>
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                    {event.task_status && <span className="capitalize">{event.task_status}</span>}
                                    {event.created_by_name && <span>• {event.created_by_name}</span>}
                                </div>
                            </div>
                        ) : (
                            <div
                                key={event.id}
                                onClick={() => openEditEventModal(event)}
                                className="p-3 rounded-xl text-white cursor-pointer hover:opacity-90 transition-opacity"
                                style={{ backgroundColor: event.color || '#34c759' }}
                            >
                                <p className="font-medium text-base">
                                    <PrepMark event={event} className="w-4 h-4 mr-1 inline-block align-text-bottom" />
                                    <DocMissingMark event={event} className="w-4 h-4 mr-1 inline-block align-text-bottom" />
                                    {event.title}
                                    {event.shooting_doc_missing && (
                                        <span className="ml-2 text-[11px] font-semibold bg-white/25 rounded px-1.5 py-0.5" title="Shooting noch nicht abgeschlossen">⚠ Abschluss fehlt</span>
                                    )}
                                </p>
                                {event.location && (
                                    <p className="text-sm opacity-80 mt-1">
                                        <AddressLink address={event.location} />
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Timed Events List */}
                <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto">
                    {selectedDayEvents.length === 0 && allDayEvents.length === 0 && dayVacations.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p className="font-medium">Keine Termine</p>
                            <p className="text-sm">Tippe um einen neuen Termin zu erstellen</p>
                            <Button 
                                onClick={() => openNewEventModal(mobileSelectedDay, 9, 0)}
                                className="mt-4 rounded-xl"
                            >
                                <Plus className="w-4 h-4 mr-2" /> Termin erstellen
                            </Button>
                        </div>
                    ) : (
                        <>
                            {selectedDayEvents
                                .sort((a, b) => parseISO(a.start) - parseISO(b.start))
                                .map(event => {
                                    const startTime = format(parseISO(event.start), 'HH:mm');
                                    const endTime = format(parseISO(event.end), 'HH:mm');
                                    
                                    return (
                                        <motion.div
                                            key={event.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            onClick={() => openEditEventModal(event)}
                                            className="flex gap-3 p-3 bg-gray-50 dark:bg-secondary/50 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary transition-colors"
                                        >
                                            {/* Color indicator & Time */}
                                            <div className="flex flex-col items-center">
                                                <div 
                                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor: event.color || '#34c759' }}
                                                />
                                                <div className="w-0.5 flex-1 bg-gray-200 dark:bg-border my-1" />
                                                <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-muted-foreground/30" />
                                            </div>
                                            
                                            {/* Event Details */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className="font-semibold text-gray-900 dark:text-white text-base leading-tight">
                                                        <PrepMark event={event} className="w-4 h-4 mr-1 inline-block align-text-bottom" />
                                                        <DocMissingMark event={event} className="w-4 h-4 mr-1 inline-block align-text-bottom" />
                                                        {event.title}
                                                    </p>
                                                    {event.recurrence && (
                                                        <RefreshCw className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                    )}
                                                </div>
                                                
                                                <p className="text-sm font-medium text-gray-600 dark:text-muted-foreground mt-1">
                                                    {startTime} - {endTime}
                                                </p>
                                                
                                                {event.description && (
                                                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                                        {event.description}
                                                    </p>
                                                )}
                                                
                                                {event.location && (
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        <AddressLink address={event.location} className="hover:text-primary" />
                                                    </p>
                                                )}
                                                
                                                {event.calendar_name && (
                                                    <Badge variant="outline" className="mt-2 text-xs" style={{ borderColor: event.color }}>
                                                        {event.calendar_name}
                                                    </Badge>
                                                )}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                        </>
                    )}
                </div>
                
                {/* Quick Add Button */}
                <div className="p-3 border-t border-gray-200 dark:border-border bg-gray-50 dark:bg-secondary/50">
                    <Button 
                        onClick={() => openNewEventModal(mobileSelectedDay, 9, 0)}
                        className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
                    >
                        <Plus className="w-4 h-4 mr-2" /> Neuer Termin
                    </Button>
                </div>
            </div>
        );
    };
    
    return (
        <>
            {/* Mobile View */}
            <div className="md:hidden">
                <MobileDayView />
            </div>
            
            {/* Desktop View */}
            <div className="hidden md:block bg-white dark:bg-card rounded-3xl shadow-xl overflow-hidden border border-gray-200/50 dark:border-border">
            {/* Day Headers */}
            <div className="grid grid-cols-8 border-b border-gray-200 dark:border-border">
                {/* Time column header */}
                <div className="p-2 text-center border-r border-gray-200 dark:border-border bg-gray-50 dark:bg-secondary/50">
                    <p className="text-xs font-medium text-gray-400">Zeit</p>
                </div>
                {weekDays.map((day, index) => (
                    <div 
                        key={index}
                        className={`p-3 text-center border-r last:border-r-0 border-gray-200 dark:border-border ${
                            isToday(day) ? 'bg-red-50 dark:bg-red-950/20' : ''
                        }`}
                    >
                        <p className="text-xs font-medium text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
                            {format(day, 'EEE', { locale: de })}
                        </p>
                        <p className={`text-2xl font-light mt-0.5 ${
                            isToday(day) ? 'text-red-500 font-medium' : 'text-gray-900 dark:text-white'
                        }`}>
                            {format(day, 'd')}
                        </p>
                    </div>
                ))}
            </div>
            
            {/* Vacation Rows */}
            {vacationRows.length > 0 && (
                <div className="border-b border-gray-200 dark:border-border">
                    {vacationRows.map((row, rowIndex) => (
                        <div key={rowIndex} className="grid grid-cols-8 h-7">
                            <div className="border-r border-gray-200 dark:border-border" />
                            {weekDays.map((day, dayIndex) => {
                                const vacation = row.find(v => {
                                    try {
                                        const start = parseISO(v.start_date);
                                        const end = parseISO(v.end_date);
                                        return isWithinInterval(day, { start: startOfDay(start), end: endOfDay(end) });
                                    } catch { return false; }
                                });
                                
                                if (!vacation) {
                                    return <div key={dayIndex} className="border-r last:border-r-0 border-gray-200 dark:border-border" />;
                                }
                                
                                const span = getVacationSpan(vacation, day);
                                const userName = vacation.user_name || users.find(u => u.id === vacation.user_id)?.first_name || 'Urlaub';
                                
                                const isFirstVisible = dayIndex === 0 || !row.find(v => {
                                    if (v.id !== vacation.id) return false;
                                    const prevDay = weekDays[dayIndex - 1];
                                    if (!prevDay) return false;
                                    try {
                                        const start = parseISO(v.start_date);
                                        const end = parseISO(v.end_date);
                                        return isWithinInterval(prevDay, { start: startOfDay(start), end: endOfDay(end) });
                                    } catch { return false; }
                                });
                                
                                return (
                                    <div key={dayIndex} className="border-r last:border-r-0 border-gray-200 dark:border-border flex items-center px-0.5">
                                        <div
                                            className={`h-5 w-full flex items-center text-xs font-medium text-white bg-gradient-to-r from-teal-500 to-emerald-500 ${
                                                span.isStart ? 'rounded-l-full pl-2' : ''
                                            } ${span.isEnd ? 'rounded-r-full pr-1' : ''}`}
                                        >
                                            {(span.isStart || isFirstVisible) && (
                                                <span className="flex items-center gap-1 truncate">
                                                    <Palmtree className="w-3 h-3 flex-shrink-0" />
                                                    <span className="truncate text-xs">{userName}</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}

            {/* All-Day Events / Tasks Row (Desktop) - mirrors mobile "Ganztägig / Tasks" section */}
            {(() => {
                const dayHasAllDay = weekDays.some(d => getEventsForDay(d).filter(e => e.all_day).length > 0);
                if (!dayHasAllDay) return null;
                return (
                    <div className="grid grid-cols-8 border-b border-gray-200 dark:border-border bg-gray-50/40 dark:bg-secondary/20" data-testid="week-allday-row">
                        <div className="border-r border-gray-200 dark:border-border flex items-center justify-end pr-2 py-1.5">
                            <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Ganztägig</span>
                        </div>
                        {weekDays.map((day, dayIndex) => {
                            const allDayEvents = getEventsForDay(day).filter(e => e.all_day);
                            return (
                                <div
                                    key={dayIndex}
                                    className="border-r last:border-r-0 border-gray-200 dark:border-border p-1 space-y-1 min-h-[28px]"
                                >
                                    {allDayEvents.map((event) => (
                                        <div
                                            key={event.id}
                                            onClick={(e) => { e.stopPropagation(); openEditEventModal(event); }}
                                            className="cursor-pointer rounded-md px-1.5 py-1 text-[11px] font-medium truncate flex items-center gap-1 hover:opacity-80 transition-opacity"
                                            style={{
                                                backgroundColor: `${event.color || '#34c759'}1F`,
                                                color: event.color || '#34c759',
                                                borderLeft: `3px solid ${event.color || '#34c759'}`,
                                            }}
                                            title={event.title}
                                            data-testid={`week-allday-event-${event.id}`}
                                        >
                                            {event.is_task ? (
                                                <CheckSquare className="w-3 h-3 flex-shrink-0" />
                                            ) : (
                                                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: event.color || '#34c759' }} />
                                            )}
                                            <PrepMark event={event} className="w-3 h-3 shrink-0" />
                                            <DocMissingMark event={event} className="w-3 h-3 shrink-0" />
                                            <span className="truncate">{event.title}</span>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                );
            })()}

            {/* Time Grid with Hours */}
            <div className="overflow-y-auto flex-1" style={{ minHeight: 'calc(100vh - 380px)', maxHeight: 'calc(100vh - 280px)' }}>
                <div className="grid grid-cols-8 relative" style={{ height: `${hours.length * 50}px` }}>
                    {/* Time Labels Column */}
                    <div className="border-r border-gray-200 dark:border-border relative bg-gray-50/50 dark:bg-secondary/30">
                        {hours.map((hour) => (
                            <div 
                                key={hour}
                                className="absolute w-full h-[50px] border-b border-gray-100 dark:border-border flex items-start justify-end pr-2 pt-1"
                                style={{ top: `${hour * 50}px` }}
                            >
                                <span className="text-xs text-gray-400 font-medium">
                                    {hour.toString().padStart(2, '0')}:00
                                </span>
                            </div>
                        ))}
                    </div>
                    
                    {/* Day Columns */}
                    {weekDays.map((day, dayIndex) => {
                        const dayEvents = getEventsForDay(day).filter(e => !e.all_day);
                        
                        return (
                            <div 
                                key={dayIndex}
                                className={`relative border-r last:border-r-0 border-gray-200 dark:border-border ${
                                    isToday(day) ? 'bg-red-50/30 dark:bg-red-900/5' : ''
                                }`}
                            >
                                {/* Hour lines */}
                                {hours.map((hour) => (
                                    <div key={hour}>
                                        {/* Full hour line */}
                                        <div 
                                            className="absolute w-full border-b border-gray-200 dark:border-border"
                                            style={{ top: `${hour * 50}px`, height: '0px' }}
                                        />
                                        {/* Half hour line (dashed) */}
                                        <div 
                                            className="absolute w-full border-b border-gray-100 dark:border-border border-dashed"
                                            style={{ top: `${hour * 50 + 25}px`, height: '0px' }}
                                        />
                                    </div>
                                ))}
                                
                                {/* Clickable 30-minute slots */}
                                {hours.map((hour) => (
                                    <React.Fragment key={`slots-${hour}`}>
                                        {/* First half hour (00) */}
                                        <div
                                            className={`absolute w-full h-[25px] cursor-pointer transition-colors ${
                                                dragOverSlot === `${dayIndex}-${hour}-0` 
                                                    ? 'bg-blue-100 dark:bg-primary/20' 
                                                    : 'hover:bg-gray-100/50 dark:hover:bg-secondary/30'
                                            }`}
                                            style={{ top: `${hour * 50}px` }}
                                            onClick={() => openNewEventModal(day, hour, 0)}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                setDragOverSlot(`${dayIndex}-${hour}-0`);
                                            }}
                                            onDragLeave={() => setDragOverSlot(null)}
                                            onDrop={(e) => handleDrop(e, day, hour, 0)}
                                        />
                                        {/* Second half hour (30) */}
                                        <div
                                            className={`absolute w-full h-[25px] cursor-pointer transition-colors ${
                                                dragOverSlot === `${dayIndex}-${hour}-30` 
                                                    ? 'bg-blue-100 dark:bg-primary/20' 
                                                    : 'hover:bg-gray-100/50 dark:hover:bg-secondary/30'
                                            }`}
                                            style={{ top: `${hour * 50 + 25}px` }}
                                            onClick={() => openNewEventModal(day, hour, 30)}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                setDragOverSlot(`${dayIndex}-${hour}-30`);
                                            }}
                                            onDragLeave={() => setDragOverSlot(null)}
                                            onDrop={(e) => handleDrop(e, day, hour, 30)}
                                        />
                                    </React.Fragment>
                                ))}
                                
                                {/* Events with overlap layout */}
                                {(() => {
                                    const timed = dayEvents.filter(e => !e.all_day);
                                    const overlapLayout = computeOverlapLayout(timed);
                                    return timed.map((event) => {
                                        const style = getEventStyle(event);
                                        const eventHeight = parseInt(style.height) || 50;
                                        const isCompact = eventHeight < 45;
                                        const hasParticipants = event.participants && event.participants.length > 0;
                                        const overlap = overlapLayout[event.id] || { leftPct: 0, widthPct: 100, total: 1, col: 0 };
                                        const eventColor = event.color || '#34c759';
                                        const calName = event.calendar_name || (connectedCalendars.find(c => c.id === event.calendar_id)?.name);
                                        // Apple/Google-style: events in the same cluster share width equally.
                                        // For dense clusters (3+) we still want each visible — give a small overlap to the right neighbour for context.
                                        // For dense clusters: compact layout where width is small
                                        const isNarrow = overlap.total >= 3;
                                        const showCalBadge = calName && !isCompact && !isNarrow;
                                        // Tiny overlap with the right neighbour for a softer apple-like look
                                        const overlapsRight = overlap.col < overlap.total - 1;
                                        const widthPct = overlap.total === 1 ? 100 : overlap.widthPct + (overlapsRight ? 2 : 0);
                                        return (
                                        <motion.div
                                            key={event.id}
                                            initial={{ opacity: 0, scale: 0.95 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            draggable
                                            onDragStart={() => setDraggedEvent(event)}
                                            onDragEnd={() => setDraggedEvent(null)}
                                            className="absolute rounded-lg p-1.5 text-xs cursor-grab active:cursor-grabbing transition-all hover:shadow-lg hover:z-30 overflow-hidden group"
                                            style={{
                                                ...style,
                                                left: `calc(${overlap.leftPct}% + 1px)`,
                                                width: `calc(${widthPct}% - 2px)`,
                                                backgroundColor: `${eventColor}1F`,
                                                color: eventColor,
                                                borderLeft: `3px solid ${eventColor}`,
                                                zIndex: 10 + overlap.col,
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openEditEventModal(event);
                                            }}
                                        >
                                            {/* Title and Time - Always visible */}
                                            <div className="flex items-start justify-between gap-1">
                                                <div className="flex items-center gap-1 min-w-0 flex-1">
                                                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: eventColor }} />
                                                    <PrepMark event={event} className="w-3 h-3 shrink-0" />
                                                    <DocMissingMark event={event} className="w-3 h-3 shrink-0" />
                                                    <p className="font-semibold truncate text-xs leading-tight">{event.title}</p>
                                                </div>
                                                {event.recurrence && (
                                                    <RefreshCw className="w-2.5 h-2.5 opacity-60 shrink-0 mt-0.5" />
                                                )}
                                            </div>
                                            <p className="opacity-75 text-[10px] font-medium ml-2.5">
                                                {format(parseISO(event.start), 'HH:mm')} - {format(parseISO(event.end), 'HH:mm')}
                                            </p>

                                            {/* Location - Show if space available */}
                                            {!isCompact && event.location && (
                                                <div className="flex items-center gap-0.5 opacity-75 mt-0.5 ml-2.5">
                                                    <MapPin className="w-2.5 h-2.5 shrink-0" />
                                                    <span className="truncate text-[10px]">{event.location}</span>
                                                </div>
                                            )}

                                            {/* Participants - Show if space available */}
                                            {!isCompact && hasParticipants && (
                                                <div className="flex items-center gap-0.5 opacity-75 mt-0.5 ml-2.5">
                                                    <Users className="w-2.5 h-2.5 shrink-0" />
                                                    <span className="text-[10px]">{event.participants.length} Teilnehmer</span>
                                                </div>
                                            )}

                                            {/* Compact indicators for small events */}
                                            {isCompact && (event.location || hasParticipants) && (
                                                <div className="flex items-center gap-1 mt-0.5 opacity-75 ml-2.5">
                                                    {event.location && <MapPin className="w-2 h-2" />}
                                                    {hasParticipants && <Users className="w-2 h-2" />}
                                                </div>
                                            )}

                                            {/* Calendar name badge — white pill, color text */}
                                            {showCalBadge && (
                                                <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-white/85 dark:bg-black/40" style={{ color: eventColor }}>
                                                    {calName}
                                                </div>
                                            )}
                                        </motion.div>
                                    );
                                    });
                                })()}
                                
                                {/* Current time indicator */}
                                {isToday(day) && (
                                    <div 
                                        className="absolute left-0 right-0 h-0.5 bg-red-500 z-20 pointer-events-none"
                                        style={{ top: `${new Date().getHours() * 50 + (new Date().getMinutes() / 60) * 50}px` }}
                                    >
                                        <div className="absolute -left-1 -top-1.5 w-3 h-3 rounded-full bg-red-500" />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
        </>
    );
};

// Month View Component
const MonthView = ({ days, currentDate, events, isToday, getEventsForDay, openNewEventModal, openEditEventModal }) => {
    const weekDayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    
    return (
        <div className="bg-white dark:bg-card rounded-3xl shadow-xl overflow-hidden border border-gray-200/50 dark:border-border">
            {/* Week Day Headers */}
            <div className="grid grid-cols-7 border-b border-gray-200 dark:border-border">
                {weekDayNames.map((name, i) => (
                    <div key={i} className="p-3 text-center text-xs font-medium text-gray-500 dark:text-muted-foreground uppercase">
                        {name}
                    </div>
                ))}
            </div>
            
            {/* Days Grid */}
            <div className="grid grid-cols-7">
                {days.map((day, index) => {
                    const dayEvents = getEventsForDay(day);
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    
                    return (
                        <div 
                            key={index}
                            className={`min-h-[120px] p-2 border-b border-r border-gray-200 dark:border-border cursor-pointer transition-colors ${
                                isToday(day) ? 'bg-red-50 dark:bg-red-950/20' : ''
                            } ${!isCurrentMonth ? 'bg-gray-50 dark:bg-secondary/30' : ''} hover:bg-gray-50 dark:hover:bg-secondary/50`}
                            onClick={() => openNewEventModal(day)}
                        >
                            <p className={`text-sm font-medium mb-1 ${
                                isToday(day) ? 'text-red-500' : 
                                !isCurrentMonth ? 'text-gray-400 dark:text-muted-foreground/50' : 'text-gray-900 dark:text-white'
                            }`}>
                                {format(day, 'd')}
                            </p>
                            <div className="space-y-1">
                                {dayEvents.slice(0, 3).map((event) => {
                                    const startTime = !event.all_day ? format(parseISO(event.start), 'HH:mm') : null;
                                    const hasParticipants = event.participants && event.participants.length > 0;
                                    const isTask = event.is_task;
                                    
                                    return (
                                        <div 
                                            key={event.id}
                                            className="text-xs p-1.5 rounded cursor-pointer hover:opacity-80 transition-opacity group relative"
                                            style={{
                                                backgroundColor: `${event.color || '#34c759'}1F`,
                                                color: event.color || '#34c759',
                                                borderLeft: `3px solid ${event.color || '#34c759'}`,
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openEditEventModal(event);
                                            }}
                                        >
                                            {/* Time + Title */}
                                            <div className="flex items-center gap-1">
                                                {isTask ? (
                                                    <CheckSquare className="w-2.5 h-2.5 shrink-0" />
                                                ) : (
                                                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: event.color || '#34c759' }} />
                                                )}
                                                {startTime && (
                                                    <span className="opacity-75 font-medium shrink-0 text-[10px]">{startTime}</span>
                                                )}
                                                <PrepMark event={event} className="w-3 h-3 shrink-0" />
                                                <DocMissingMark event={event} className="w-3 h-3 shrink-0" />
                                                <span className="font-medium truncate">{event.title}</span>
                                            </div>
                                            
                                            {/* Location */}
                                            {event.location && (
                                                <div className="flex items-center gap-0.5 opacity-80 mt-0.5 truncate">
                                                    <MapPin className="w-2.5 h-2.5 shrink-0" />
                                                    <span className="truncate text-[10px]">{event.location}</span>
                                                </div>
                                            )}
                                            
                                            {/* Indicators */}
                                            <div className="flex items-center gap-1 mt-0.5">
                                                {hasParticipants && (
                                                    <div className="flex items-center gap-0.5 opacity-80">
                                                        <Users className="w-2.5 h-2.5" />
                                                        <span className="text-[10px]">{event.participants.length}</span>
                                                    </div>
                                                )}
                                                {event.recurrence && (
                                                    <RefreshCw className="w-2.5 h-2.5 opacity-80" />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {dayEvents.length > 3 && (
                                    <p className="text-xs text-gray-500 font-medium">+{dayEvents.length - 3} weitere</p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Day View Component with hour grid
const DayView = ({ currentDate, events, vacations, hours, openNewEventModal, openEditEventModal, onDragEnd }) => {
    const [dragOverSlot, setDragOverSlot] = useState(null);
    
    // Create 30-minute slots for more precise drag & drop
    const timeSlots = [];
    for (let hour = 0; hour < 24; hour++) {
        timeSlots.push({ hour, minute: 0 });
        timeSlots.push({ hour, minute: 30 });
    }
    
    // Get event position for day view (50px per hour)
    const getEventStyle = (event) => {
        try {
            const start = parseISO(event.start);
            const end = parseISO(event.end);
            const startHour = getHours(start);
            const startMinute = getMinutes(start);
            const endHour = getHours(end);
            const endMinute = getMinutes(end);
            
            const top = startHour * 50 + (startMinute / 60) * 50;
            const duration = (endHour - startHour) * 50 + ((endMinute - startMinute) / 60) * 50;
            
            return {
                top: `${top}px`,
                height: `${Math.max(duration, 20)}px`,
            };
        } catch {
            return { top: '0px', height: '50px' };
        }
    };
    
    const handleDrop = (e, hour, minute) => {
        e.preventDefault();
        setDragOverSlot(null);
        const eventData = e.dataTransfer.getData('event');
        if (eventData) {
            onDragEnd(JSON.parse(eventData), currentDate, hour, minute);
        }
    };
    
    return (
        <div className="bg-white dark:bg-card rounded-3xl shadow-xl overflow-hidden border border-gray-200/50 dark:border-border flex flex-col" style={{ height: 'calc(100vh - 250px)' }}>
            {/* Vacation Banner */}
            {vacations.length > 0 && (
                <div className="p-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white flex items-center gap-2 flex-shrink-0">
                    <Palmtree className="w-5 h-5" />
                    <span>Urlaub: {vacations.map(v => v.user_name || 'Jemand').join(', ')}</span>
                </div>
            )}
            
            {/* Hour Grid with 30-minute slots */}
            <div className="relative overflow-y-auto flex-1" style={{ minHeight: '400px' }}>
                <div className="relative" style={{ height: '1200px' }}>
                    {/* Hour lines */}
                    {hours.map((hour) => (
                        <div 
                            key={hour}
                            className="absolute w-full border-b border-gray-200 dark:border-border"
                            style={{ top: `${hour * 50}px`, height: '50px' }}
                        >
                            <div className="w-16 text-xs text-gray-500 dark:text-muted-foreground p-2 border-r border-gray-200 dark:border-border bg-gray-50/50 dark:bg-secondary/30 h-full flex items-start">
                                {hour.toString().padStart(2, '0')}:00
                            </div>
                        </div>
                    ))}
                    
                    {/* Half-hour lines (thinner) */}
                    {hours.map((hour) => (
                        <div 
                            key={`half-${hour}`}
                            className="absolute w-full border-b border-gray-100 dark:border-border border-dashed"
                            style={{ top: `${hour * 50 + 25}px`, height: '0px', left: '64px' }}
                        />
                    ))}
                    
                    {/* Clickable/Droppable time slots (every 30 minutes) */}
                    {timeSlots.map(({ hour, minute }, index) => (
                        <div
                            key={`slot-${hour}-${minute}`}
                            className={`absolute h-[25px] cursor-pointer transition-colors ${
                                dragOverSlot === `${hour}-${minute}` 
                                    ? 'bg-blue-100 dark:bg-primary/15' 
                                    : 'hover:bg-gray-50 dark:hover:bg-secondary/20'
                            }`}
                            style={{ top: `${hour * 50 + (minute / 30) * 25}px`, left: '64px', right: 0 }}
                            onClick={() => openNewEventModal(currentDate, hour, minute)}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOverSlot(`${hour}-${minute}`);
                            }}
                            onDragLeave={() => setDragOverSlot(null)}
                            onDrop={(e) => handleDrop(e, hour, minute)}
                        />
                    ))}
                    
                    {/* Current time indicator */}
                    {isSameDay(currentDate, new Date()) && (
                        <div 
                            className="absolute left-16 right-0 h-0.5 bg-red-500 z-20"
                            style={{ top: `${new Date().getHours() * 50 + (new Date().getMinutes() / 60) * 50}px` }}
                        >
                            <div className="absolute -left-2 -top-1.5 w-3 h-3 rounded-full bg-red-500" />
                        </div>
                    )}
                    
                    {/* Events */}
                    {events.filter(e => !e.all_day).map((event) => {
                        const style = getEventStyle(event);
                        const eventHeight = parseInt(style.height) || 50;
                        const isLarge = eventHeight >= 60;
                        const hasParticipants = event.participants && event.participants.length > 0;
                        
                        return (
                        <motion.div
                            key={event.id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="absolute left-20 right-2 rounded-xl p-2 text-white text-sm cursor-grab active:cursor-grabbing hover:opacity-90 shadow-md overflow-hidden z-10"
                            style={{ 
                                ...style,
                                backgroundColor: event.color || '#34c759'
                            }}
                            draggable
                            onDragStart={(e) => e.dataTransfer.setData('event', JSON.stringify(event))}
                            onClick={() => openEditEventModal(event)}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <p className="font-semibold truncate"><PrepMark event={event} className="w-4 h-4 mr-1 inline-block align-text-bottom" /><DocMissingMark event={event} className="w-4 h-4 mr-1 inline-block align-text-bottom" />{event.title}</p>
                                {event.recurrence && (
                                    <RefreshCw className="w-3.5 h-3.5 opacity-70 shrink-0 mt-0.5" />
                                )}
                            </div>
                            <p className="text-xs opacity-80 font-medium">
                                {format(parseISO(event.start), 'HH:mm')} - {format(parseISO(event.end), 'HH:mm')}
                            </p>
                            {event.location && (
                                <p className="text-xs opacity-80 mt-1 flex min-w-0">
                                    <AddressLink address={event.location} className="min-w-0" />
                                </p>
                            )}
                            {isLarge && hasParticipants && (
                                <p className="text-xs opacity-80 flex items-center gap-1 mt-1">
                                    <Users className="w-3 h-3 shrink-0" /> 
                                    <span>{event.participants.length} Teilnehmer</span>
                                </p>
                            )}
                            {isLarge && event.description && (
                                <p className="text-xs opacity-70 mt-1 line-clamp-2">
                                    {event.description}
                                </p>
                            )}
                            {!isLarge && (hasParticipants || event.description) && (
                                <div className="flex items-center gap-1 mt-1 opacity-70">
                                    {hasParticipants && <Users className="w-2.5 h-2.5" />}
                                </div>
                            )}
                        </motion.div>
                    );
                })}
                
                {/* All-day events banner */}
                {events.filter(e => e.all_day).length > 0 && (
                    <div className="absolute top-0 left-16 right-0 p-2 space-y-1 bg-gray-50 dark:bg-secondary/50 border-b border-gray-200 dark:border-border">
                        {events.filter(e => e.all_day).map(event => (
                            <div 
                                key={event.id}
                                className="text-xs px-2 py-1.5 rounded-lg cursor-pointer hover:opacity-90 flex items-center gap-2 font-medium"
                                style={{
                                    backgroundColor: event.is_task ? `${event.color || '#5856D6'}20` : (event.color || '#34c759'),
                                    color: event.is_task ? (event.color || '#5856D6') : '#ffffff',
                                    border: event.is_task ? `1px dashed ${event.color || '#5856D6'}` : 'none',
                                }}
                                onClick={() => openEditEventModal(event)}
                            >
                                {event.is_task && <CheckSquare className="w-3.5 h-3.5 flex-shrink-0" />}
                                <PrepMark event={event} className="w-3.5 h-3.5 flex-shrink-0" />
                                <DocMissingMark event={event} className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="truncate">{event.title}</span>
                            </div>
                        ))}
                    </div>
                )}
                </div>
            </div>
        </div>
    );
};

// Event Modal Component
const EventModal = ({ open, onOpenChange, eventForm, setEventForm, selectedEvent, users, connectedCalendars = [], customers = [], onSave, onDelete, onCompleteShooting }) => {
    const [showStartCalendar, setShowStartCalendar] = useState(false);
    const [showEndCalendar, setShowEndCalendar] = useState(false);
    const { api } = useAuth();
    const plannedFaces = eventForm.planned_faces || [];

    // Beim Bearbeiten kommen nur IDs vom Server - Namen der geplanten Social Faces nachladen
    useEffect(() => {
        const unnamed = plannedFaces.filter(f => !f.name).map(f => f.id);
        if (!open || unnamed.length === 0) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('/forms/shooting/faces', { params: { ids: unnamed.join(',') } });
                if (cancelled) return;
                const names = Object.fromEntries((res.data || []).map(f => [f.id, f.name]));
                setEventForm(f => ({
                    ...f,
                    planned_faces: (f.planned_faces || []).map(pf => pf.name ? pf : { ...pf, name: names[pf.id] || 'Unbekannt' })
                }));
            } catch (e) { /* Namen bleiben leer */ }
        })();
        return () => { cancelled = true; };
    }, [open, plannedFaces.map(f => f.id + (f.name ? '1' : '0')).join(',')]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[92vw] max-w-md max-h-[80vh] overflow-y-auto rounded-2xl p-4 sm:p-5 overflow-x-hidden">
                <DialogHeader className="pb-2">
                    <DialogTitle className="flex items-center gap-2 text-base font-semibold">
                        {selectedEvent ? <Edit3 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                        {selectedEvent ? 'Bearbeiten' : 'Neuer Termin'}
                    </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-3">
                    {/* Title */}
                    <Input
                        value={eventForm.title}
                        onChange={(e) => setEventForm(f => ({ ...f, title: e.target.value }))}
                        placeholder="Titel eingeben..."
                        className="rounded-xl h-10 text-sm font-medium border-gray-200 dark:border-border"
                    />
                    
                    {/* All Day Toggle + Date/Time Row */}
                    <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1.5 cursor-pointer shrink-0">
                            <input
                                type="checkbox"
                                checked={eventForm.all_day}
                                onChange={(e) => setEventForm(f => ({ ...f, all_day: e.target.checked }))}
                                className="rounded w-3.5 h-3.5 accent-red-500"
                            />
                            <span className="text-xs text-muted-foreground">Ganztägig</span>
                        </label>
                    </div>
                    
                    {/* Start Date/Time */}
                    <div className="grid grid-cols-2 gap-2">
                        <div className="relative">
                            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">Start</Label>
                            <button
                                type="button"
                                onClick={() => setShowStartCalendar(!showStartCalendar)}
                                className="w-full h-9 px-2.5 text-left text-sm rounded-lg border border-gray-200 dark:border-border bg-background hover:bg-muted/50 flex items-center justify-between"
                            >
                                <span className={eventForm.start_date ? '' : 'text-muted-foreground'}>
                                    {eventForm.start_date ? format(parseISO(eventForm.start_date), 'dd.MM.yyyy') : 'Datum'}
                                </span>
                                <CalendarIcon className="w-3.5 h-3.5 text-muted-foreground" />
                            </button>
                            {showStartCalendar && (
                                <div className="absolute z-50 mt-1 bg-background border rounded-xl shadow-lg p-2">
                                    <Calendar
                                        mode="single"
                                        selected={eventForm.start_date ? parseISO(eventForm.start_date) : undefined}
                                        onSelect={(date) => {
                                            if (date) {
                                                const dateStr = format(date, 'yyyy-MM-dd');
                                                setEventForm(f => ({ 
                                                    ...f, 
                                                    start_date: dateStr,
                                                    end_date: f.end_date || dateStr
                                                }));
                                            }
                                            setShowStartCalendar(false);
                                        }}
                                        className="rounded-lg"
                                    />
                                </div>
                            )}
                        </div>
                        {!eventForm.all_day && (
                            <div>
                                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">Zeit</Label>
                                <Input 
                                    type="time" 
                                    value={eventForm.start_time} 
                                    onChange={(e) => setEventForm(f => ({ ...f, start_time: e.target.value }))} 
                                    className="rounded-lg h-9 text-sm" 
                                />
                            </div>
                        )}
                    </div>
                    
                    {/* End Date/Time */}
                    <div className="grid grid-cols-2 gap-2">
                        <div className="relative">
                            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">Ende</Label>
                            <button
                                type="button"
                                onClick={() => setShowEndCalendar(!showEndCalendar)}
                                className="w-full h-9 px-2.5 text-left text-sm rounded-lg border border-gray-200 dark:border-border bg-background hover:bg-muted/50 flex items-center justify-between"
                            >
                                <span className={eventForm.end_date ? '' : 'text-muted-foreground'}>
                                    {eventForm.end_date ? format(parseISO(eventForm.end_date), 'dd.MM.yyyy') : 'Datum'}
                                </span>
                                <CalendarIcon className="w-3.5 h-3.5 text-muted-foreground" />
                            </button>
                            {showEndCalendar && (
                                <div className="absolute z-50 mt-1 bg-background border rounded-xl shadow-lg p-2">
                                    <Calendar
                                        mode="single"
                                        selected={eventForm.end_date ? parseISO(eventForm.end_date) : undefined}
                                        onSelect={(date) => {
                                            if (date) {
                                                setEventForm(f => ({ ...f, end_date: format(date, 'yyyy-MM-dd') }));
                                            }
                                            setShowEndCalendar(false);
                                        }}
                                        className="rounded-lg"
                                    />
                                </div>
                            )}
                        </div>
                        {!eventForm.all_day && (
                            <div>
                                <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">Zeit</Label>
                                <Input 
                                    type="time" 
                                    value={eventForm.end_time} 
                                    onChange={(e) => setEventForm(f => ({ ...f, end_time: e.target.value }))} 
                                    className="rounded-lg h-9 text-sm" 
                                />
                            </div>
                        )}
                    </div>
                    
                    {/* Location */}
                    <div>
                        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">Ort</Label>
                        <AddressInput
                            value={eventForm.location}
                            onChange={(v) => setEventForm(f => ({ ...f, location: v }))}
                            placeholder="Adresse (Vorschläge von OpenStreetMap)"
                            className="rounded-lg h-9 text-sm"
                            data-testid="event-location-input"
                        />
                    </div>

                    <div>
                        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">Online-Meeting Link</Label>
                        <Input 
                            value={eventForm.meeting_link} 
                            onChange={(e) => setEventForm(f => ({ ...f, meeting_link: e.target.value }))} 
                            placeholder="https://meet.google.com/... (optional)" 
                            className="rounded-lg h-9 text-sm" 
                        />
                    </div>
                    
                    {/* Description - Collapsible */}
                    <div>
                        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">Notizen</Label>
                        <Textarea 
                            value={eventForm.description} 
                            onChange={(e) => setEventForm(f => ({ ...f, description: e.target.value }))} 
                            placeholder="Optional..." 
                            className="rounded-lg text-sm min-h-[60px] resize-none" 
                            rows={2} 
                        />
                    </div>
                    
                    {/* Recurrence & Reminder - Compact Row */}
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                                <RefreshCw className="w-3 h-3" /> Wiederholen
                            </Label>
                            <Select value={eventForm.recurrence} onValueChange={(v) => setEventForm(f => ({ ...f, recurrence: v }))}>
                                <SelectTrigger className="rounded-lg h-9 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {RECURRENCE_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                                <Bell className="w-3 h-3" /> Erinnerung
                            </Label>
                            <Select value={eventForm.reminder} onValueChange={(v) => setEventForm(f => ({ ...f, reminder: v }))}>
                                <SelectTrigger className="rounded-lg h-9 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {REMINDER_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Travel Time + Combined Reminder Info */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> Wegzeit
                            </Label>
                            <Select value={eventForm.travel_time} onValueChange={(v) => setEventForm(f => ({ ...f, travel_time: v }))}>
                                <SelectTrigger className="rounded-lg h-9 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TRAVEL_TIME_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        {eventForm.reminder !== 'none' && eventForm.travel_time !== '0' && (
                            <div className="flex items-end pb-1">
                                <p className="text-[10px] text-primary font-medium">
                                    Erinnerung {parseInt(eventForm.reminder) + parseInt(eventForm.travel_time)} Min vorher (inkl. Wegzeit)
                                </p>
                            </div>
                        )}
                    </div>
                    
                    {eventForm.recurrence !== 'none' && (
                        <div>
                            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">Ende Wiederholung</Label>
                            <Input type="date" value={eventForm.recurrence_end} onChange={(e) => setEventForm(f => ({ ...f, recurrence_end: e.target.value }))} className="rounded-lg h-9 text-sm" />
                        </div>
                    )}
                    
                    {/* Calendar Selection (also when editing) — color follows calendar */}
                    {connectedCalendars.length > 0 && (
                        <div>
                            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5 block">Kalender</Label>
                            <div className="flex gap-1.5 flex-wrap">
                                {connectedCalendars.map(cal => (
                                    <button
                                        key={cal.id}
                                        type="button"
                                        onClick={() => setEventForm(f => ({ ...f, calendar_id: cal.id, color: cal.color }))}
                                        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                                            eventForm.calendar_id === cal.id
                                                ? 'ring-2 ring-offset-1 shadow-sm'
                                                : 'opacity-60 hover:opacity-100'
                                        }`}
                                        style={{
                                            backgroundColor: eventForm.calendar_id === cal.id ? cal.color + '25' : 'transparent',
                                            color: cal.color,
                                            borderWidth: 1,
                                            borderColor: cal.color + '50'
                                        }}
                                        data-testid={`calendar-pick-${cal.id}`}
                                    >
                                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cal.color }} />
                                        {cal.name}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-1.5">Die Farbe wird automatisch vom Kalender übernommen.</p>
                        </div>
                    )}

                    {/* Customer link (optional) — syncs event to PlanFlow */}
                    <div>
                        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5 block">
                            Kunde <span className="normal-case text-muted-foreground/70">{eventForm.is_shooting ? '(Pflicht bei Shootings)' : '(optional – synchronisiert an PlanFlow)'}</span>
                        </Label>
                        <div className="flex items-center gap-2">
                            <div className="flex-1">
                                <CustomerPicker
                                    customers={customers}
                                    value={eventForm.customer_id}
                                    onChange={(id) => setEventForm(f => ({ ...f, customer_id: id }))}
                                    placeholder="Kein Kundenbezug"
                                    triggerClassName="h-9 text-sm"
                                    testId="event-customer-picker"
                                />
                            </div>
                            {eventForm.customer_id && (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEventForm(f => ({ ...f, customer_id: '' }))}
                                    className="h-9 px-2"
                                    data-testid="event-customer-clear"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </Button>
                            )}
                        </div>
                    </div>
                    
                    {/* Shooting-Planung: legt ein SF-Shooting an, die Doku bestaetigt es spaeter */}
                    <div className={`rounded-xl border p-3 flex items-center justify-between gap-3 ${eventForm.is_prepared ? 'border-green-500/50 bg-green-500/10' : 'border-border/60'}`} data-testid="event-prepared-block">
                        <div className="flex items-center gap-2">
                            <CheckCircle2 className={`w-5 h-5 shrink-0 ${eventForm.is_prepared ? 'text-green-500' : 'text-muted-foreground/40'}`} />
                            <div>
                                <Label className="text-sm font-medium">Vorbereitung erledigt</Label>
                                <p className="text-[10px] text-muted-foreground">Moodboard verschickt, Social Faces fix, nichts mehr offen – zeigt einen grünen Haken im Kalender</p>
                            </div>
                        </div>
                        <Switch
                            checked={!!eventForm.is_prepared}
                            onCheckedChange={(v) => setEventForm(f => ({ ...f, is_prepared: v }))}
                            data-testid="event-is-prepared"
                        />
                    </div>
                    <div className={`rounded-xl border p-3 space-y-3 ${eventForm.is_shooting ? 'border-primary/40 bg-primary/5' : 'border-border/60'}`} data-testid="event-shooting-block">
                        <div className="flex items-center justify-between">
                            <div>
                                <Label className="text-sm font-medium">Shooting-Termin</Label>
                                <p className="text-[10px] text-muted-foreground">Plant das Shooting für Doku und Social-Faces-Abrechnung</p>
                            </div>
                            <Switch
                                checked={!!eventForm.is_shooting}
                                onCheckedChange={(v) => setEventForm(f => ({ ...f, is_shooting: v }))}
                                data-testid="event-is-shooting"
                            />
                        </div>
                        {eventForm.is_shooting && (
                            <>
                                {!eventForm.customer_id && (
                                    <p className="text-[11px] text-amber-500">Bitte oben einen Kunden wählen, sonst kann das Shooting nicht gespeichert werden.</p>
                                )}
                                <div>
                                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">Shootingleitung</Label>
                                    <Select
                                        value={eventForm.shooting_lead_user_id || 'me'}
                                        onValueChange={(v) => setEventForm(f => ({ ...f, shooting_lead_user_id: v === 'me' ? '' : v }))}
                                    >
                                        <SelectTrigger className="h-9 text-sm" data-testid="event-shooting-lead">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="me">Ich (Ersteller des Termins)</SelectItem>
                                            {users.filter(u => u.is_active !== false && u.role !== 'influencer').map(u => (
                                                <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-[10px] text-muted-foreground mt-1">Die Shootingleitung schließt das Shooting nach dem Termin ab und bekommt die Erinnerung. Ohne Auswahl bist du es selbst.</p>
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">Geplante Social Faces</Label>
                                    {plannedFaces.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-2">
                                            {plannedFaces.map(face => (
                                                <span key={face.id} className="inline-flex items-center gap-1 text-xs bg-background border border-border rounded-full pl-2 pr-1 py-0.5">
                                                    {face.name || '…'}
                                                    <button
                                                        type="button"
                                                        onClick={() => setEventForm(f => ({ ...f, planned_faces: (f.planned_faces || []).filter(p => p.id !== face.id) }))}
                                                        className="hover:text-destructive"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <SocialFacePicker
                                        excludeIds={plannedFaces.map(f => f.id)}
                                        onSelect={(face) => setEventForm(f => ({ ...f, planned_faces: [...(f.planned_faces || []), { id: face.id, name: face.name }] }))}
                                        inputClassName="h-9 text-sm"
                                    />
                                </div>
                            </>
                        )}
                        {selectedEvent?.is_shooting && selectedEvent?.sf_shooting_id && (
                            <div className="flex items-center justify-between gap-2 pt-1">
                                {selectedEvent.shooting_doc_missing ? (
                                    <span className="text-[11px] text-amber-500 font-medium">⚠ Shooting ist vorbei und noch nicht abgeschlossen</span>
                                ) : selectedEvent.shooting_doc_id ? (
                                    <span className="text-[11px] text-green-500 font-medium">✓ Abgeschlossen und zur Abrechnung freigegeben</span>
                                ) : <span className="text-[11px] text-muted-foreground">Nach dem Shooting hier abschließen</span>}
                                <Button
                                    type="button"
                                    size="sm"
                                    variant={selectedEvent.shooting_doc_missing ? 'default' : 'outline'}
                                    className="h-8 text-xs"
                                    onClick={() => onCompleteShooting && onCompleteShooting(selectedEvent)}
                                    data-testid="event-complete-shooting"
                                >
                                    {selectedEvent.shooting_doc_id ? 'Abschluss bearbeiten' : 'Shooting abschließen'}
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Participants - Compact */}
                    <div>
                        <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                            <Users className="w-3 h-3" /> Teilnehmer
                        </Label>
                        {eventForm.participants.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                                {eventForm.participants.map(pid => {
                                    const p = users.find(u => u.id === pid);
                                    if (!p) return null;
                                    return (
                                        <Badge key={pid} variant="secondary" className="flex items-center gap-1 pr-0.5 text-[10px] h-5">
                                            {p.first_name}
                                            <button onClick={() => setEventForm(f => ({ ...f, participants: f.participants.filter(id => id !== pid) }))} className="hover:bg-gray-300 rounded-full p-0.5">
                                                <X className="w-2.5 h-2.5" />
                                            </button>
                                        </Badge>
                                    );
                                })}
                            </div>
                        )}
                        <Select value="" onValueChange={(uid) => { if (!eventForm.participants.includes(uid)) setEventForm(f => ({ ...f, participants: [...f.participants, uid] })); }}>
                            <SelectTrigger className="rounded-lg h-9 text-xs"><SelectValue placeholder="Hinzufügen..." /></SelectTrigger>
                            <SelectContent>
                                {users.filter(u => !eventForm.participants.includes(u.id)).map(u => (
                                    <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                
                {/* Footer Buttons */}
                <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                    {selectedEvent && (
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => onDelete(selectedEvent.recurrence_parent_id || selectedEvent.id)} 
                            className="text-red-500 hover:text-red-600 hover:bg-red-50 px-2"
                        >
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    )}
                    <div className="flex-1" />
                    <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="text-muted-foreground">
                        Abbrechen
                    </Button>
                    <Button 
                        size="sm"
                        onClick={onSave} 
                        disabled={!eventForm.title || !eventForm.start_date || (eventForm.is_shooting && !eventForm.customer_id)} 
                        className="bg-primary hover:bg-primary/90 text-white px-4"
                    >
                        {selectedEvent ? 'Speichern' : 'Erstellen'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Conflicts Modal - Shows overlapping events with edit/delete options
const ConflictsModal = ({ open, onOpenChange, conflicts, onEditEvent, onDeleteEvent }) => {
    const formatEventTime = (dateStr) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
        } catch {
            return '';
        }
    };
    
    const formatEventDate = (dateStr) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' });
        } catch {
            return '';
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg rounded-3xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-red-600">
                        <AlertTriangle className="w-5 h-5" /> 
                        {conflicts.length} Kalender-Konflikt{conflicts.length > 1 ? 'e' : ''}
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        Diese Events überlappen sich zeitlich. Bearbeite oder lösche Events um Konflikte zu beheben.
                    </p>
                </DialogHeader>
                <div className="space-y-4 overflow-y-auto flex-1 pr-2">
                    {conflicts.map((conflict, idx) => (
                        <div key={idx} className="p-4 rounded-2xl border border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-950/20">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-xs font-medium text-red-600 bg-red-100 dark:bg-red-900/50 px-2 py-0.5 rounded-full">
                                    Konflikt {idx + 1}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                    {formatEventDate(conflict.event1.start)}
                                </span>
                            </div>
                            
                            {/* Event 1 */}
                            <div className="flex items-center justify-between p-3 rounded-xl bg-white dark:bg-card border border-gray-200 dark:border-border mb-2">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div 
                                        className="w-3 h-3 rounded-full flex-shrink-0" 
                                        style={{ backgroundColor: conflict.event1.color || '#3B82F6' }}
                                    />
                                    <div className="min-w-0">
                                        <p className="font-medium text-sm truncate">{conflict.event1.title}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatEventTime(conflict.event1.start)} - {formatEventTime(conflict.event1.end)}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onEditEvent(conflict.event1)}
                                        className="h-8 w-8 p-0"
                                    >
                                        <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            if (window.confirm(`"${conflict.event1.title}" wirklich löschen?`)) {
                                                onDeleteEvent(conflict.event1.id);
                                            }
                                        }}
                                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                            
                            {/* Overlap indicator */}
                            <div className="flex items-center justify-center py-1">
                                <div className="flex items-center gap-2 text-xs text-red-500">
                                    <div className="h-px w-8 bg-red-300" />
                                    <span>überschneidet sich mit</span>
                                    <div className="h-px w-8 bg-red-300" />
                                </div>
                            </div>
                            
                            {/* Event 2 */}
                            <div className="flex items-center justify-between p-3 rounded-xl bg-white dark:bg-card border border-gray-200 dark:border-border">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div 
                                        className="w-3 h-3 rounded-full flex-shrink-0" 
                                        style={{ backgroundColor: conflict.event2.color || '#3B82F6' }}
                                    />
                                    <div className="min-w-0">
                                        <p className="font-medium text-sm truncate">{conflict.event2.title}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatEventTime(conflict.event2.start)} - {formatEventTime(conflict.event2.end)}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => onEditEvent(conflict.event2)}
                                        className="h-8 w-8 p-0"
                                    >
                                        <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            if (window.confirm(`"${conflict.event2.title}" wirklich löschen?`)) {
                                                onDeleteEvent(conflict.event2.id);
                                            }
                                        }}
                                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {conflicts.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                        <p>Keine Konflikte gefunden!</p>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

// Templates Modal
const TemplatesModal = ({ open, onOpenChange, onSelect }) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md rounded-3xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Zap className="w-5 h-5" /> Schnellvorlagen
                    </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    {EVENT_TEMPLATES.map(template => (
                        <button
                            key={template.id}
                            onClick={() => onSelect(template)}
                            className="p-4 rounded-2xl border border-gray-200 dark:border-border hover:border-gray-300 dark:hover:border-gray-600 transition-all hover:shadow-md text-left"
                        >
                            <span className="text-2xl">{template.emoji}</span>
                            <p className="font-medium mt-2">{template.name}</p>
                            <p className="text-xs text-gray-500">{template.duration} Min.</p>
                        </button>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Availability Modal
const AvailabilityModal = ({ open, onOpenChange, users, events, currentDate }) => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const hours = Array.from({ length: 10 }, (_, i) => i + 8); // 8:00 - 18:00
    
    const isUserBusy = (userId, hour) => {
        return events.some(event => {
            if (!event.participants?.includes(userId) && event.user_id !== userId) return false;
            try {
                const start = parseISO(event.start);
                const end = parseISO(event.end);
                const eventHourStart = start.getHours();
                const eventHourEnd = end.getHours();
                return hour >= eventHourStart && hour < eventHourEnd && isSameDay(start, currentDate);
            } catch { return false; }
        });
    };
    
    const allAvailable = (hour) => {
        if (selectedUsers.length === 0) return true;
        return selectedUsers.every(uid => !isUserBusy(uid, hour));
    };
    
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl rounded-3xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Eye className="w-5 h-5" /> Verfügbarkeit - {format(currentDate, 'd. MMMM yyyy', { locale: de })}
                    </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                    <div>
                        <Label>Teilnehmer auswählen</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {users.map(u => (
                                <Badge 
                                    key={u.id}
                                    variant={selectedUsers.includes(u.id) ? 'default' : 'outline'}
                                    className="cursor-pointer"
                                    onClick={() => setSelectedUsers(prev => 
                                        prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id]
                                    )}
                                >
                                    {u.first_name}
                                </Badge>
                            ))}
                        </div>
                    </div>
                    
                    <div className="border rounded-xl overflow-hidden">
                        <div className="grid grid-cols-11 bg-gray-50 dark:bg-secondary">
                            <div className="p-2 border-r text-xs font-medium"></div>
                            {hours.map(h => (
                                <div key={h} className="p-2 text-xs text-center font-medium border-r last:border-r-0">
                                    {h}:00
                                </div>
                            ))}
                        </div>
                        
                        {selectedUsers.map(uid => {
                            const u = users.find(u => u.id === uid);
                            return (
                                <div key={uid} className="grid grid-cols-11 border-t">
                                    <div className="p-2 border-r text-xs truncate">{u?.first_name}</div>
                                    {hours.map(h => (
                                        <div 
                                            key={h} 
                                            className={`p-2 border-r last:border-r-0 ${
                                                isUserBusy(uid, h) ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'
                                            }`}
                                        />
                                    ))}
                                </div>
                            );
                        })}
                        
                        {selectedUsers.length > 1 && (
                            <div className="grid grid-cols-11 border-t bg-gray-50 dark:bg-secondary">
                                <div className="p-2 border-r text-xs font-medium">Alle frei</div>
                                {hours.map(h => (
                                    <div 
                                        key={h} 
                                        className={`p-2 border-r last:border-r-0 flex items-center justify-center ${
                                            allAvailable(h) ? 'bg-green-500' : 'bg-gray-200 dark:bg-border'
                                        }`}
                                    >
                                        {allAvailable(h) && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Calendar Settings Modal - Internal Calendar Management (v2)
const CalendarSettingsModal = ({ open, onOpenChange, onSave, showTasksInCalendar, setShowTasksInCalendar }) => {
    const { api, user } = useAuth();
    const [calendars, setCalendars] = useState([]);
    const [sharedCalendars, setSharedCalendars] = useState([]);
    const [newCalName, setNewCalName] = useState('');
    const [newCalColor, setNewCalColor] = useState('#3B82F6');
    const [activeTab, setActiveTab] = useState('mine');
    const [loading, setLoading] = useState(false);

    const colors = ['#3B82F6', '#EF4444', '#22C55E', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#14B8A6', '#6366F1'];

    useEffect(() => {
        if (open) fetchCalendars();
    }, [open]);

    const fetchCalendars = async () => {
        setLoading(true);
        try {
            const [mineRes, sharedRes] = await Promise.all([
                api.get('/calendar/v2/calendars'),
                api.get('/calendar/v2/calendars/shared')
            ]);
            setCalendars(mineRes.data || []);
            setSharedCalendars(sharedRes.data || []);
        } catch (e) {}
        setLoading(false);
    };

    const handleCreate = async () => {
        if (!newCalName.trim()) return;
        try {
            await api.post('/calendar/v2/calendars', { name: newCalName.trim(), color: newCalColor, is_shared: true });
            setNewCalName('');
            fetchCalendars();
            toast.success('Kalender erstellt!');
        } catch (e) { toast.error('Fehler'); }
    };

    const handleColorChange = async (calId, color) => {
        try {
            await api.put(`/calendar/v2/calendars/${calId}`, { color });
            fetchCalendars();
            onSave();
        } catch (e) { toast.error('Fehler'); }
    };

    const handleDelete = async (calId) => {
        if (!window.confirm('Kalender und alle Termine loeschen?')) return;
        try {
            await api.delete(`/calendar/v2/calendars/${calId}`);
            fetchCalendars();
            onSave();
            toast.success('Kalender geloescht');
        } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    };

    const handleSubscribe = async (calId) => {
        try {
            await api.post(`/calendar/v2/calendars/${calId}/subscribe`);
            fetchCalendars();
            onSave();
            toast.success('Kalender abonniert!');
        } catch (e) { toast.error('Fehler'); }
    };

    const handleUnsubscribe = async (calId) => {
        try {
            await api.post(`/calendar/v2/calendars/${calId}/unsubscribe`);
            fetchCalendars();
            onSave();
            toast.success('Abo entfernt');
        } catch (e) { toast.error('Fehler'); }
    };

    const handleToggleShare = async (calId, currentlyShared) => {
        try {
            await api.put(`/calendar/v2/calendars/${calId}`, { is_shared: !currentlyShared });
            fetchCalendars();
            toast.success(!currentlyShared ? 'Kalender freigegeben' : 'Freigabe entfernt');
        } catch (e) { toast.error('Fehler'); }
    };

    const subscribedIds = calendars.filter(c => c.is_subscribed).map(c => c.id);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto p-4 sm:p-6">
                <DialogHeader className="pb-2">
                    <DialogTitle className="text-base flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-primary" />
                        Kalender verwalten
                    </DialogTitle>
                </DialogHeader>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="w-full">
                        <TabsTrigger value="mine" className="flex-1 text-xs">Meine Kalender</TabsTrigger>
                        <TabsTrigger value="shared" className="flex-1 text-xs">Verfuegbare Kalender</TabsTrigger>
                    </TabsList>

                    <TabsContent value="mine" className="space-y-3 mt-3">
                        {/* Create new calendar */}
                        <div className="flex gap-2">
                            <Input
                                value={newCalName}
                                onChange={(e) => setNewCalName(e.target.value)}
                                placeholder="Neuer Kalender..."
                                className="flex-1 h-9"
                                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                                data-testid="new-calendar-input"
                            />
                            <div className="flex items-center gap-1">
                                {colors.slice(0, 5).map(c => (
                                    <button
                                        key={c}
                                        onClick={() => setNewCalColor(c)}
                                        className={`w-5 h-5 rounded-full transition-transform ${newCalColor === c ? 'ring-2 ring-offset-1 ring-gray-400 scale-110' : ''}`}
                                        style={{ backgroundColor: c }}
                                    />
                                ))}
                            </div>
                            <Button size="sm" className="h-9" onClick={handleCreate} data-testid="create-calendar-btn">
                                <Plus className="w-4 h-4" />
                            </Button>
                        </div>

                        {/* Calendar list */}
                        <div className="space-y-1.5">
                            {calendars.map(cal => (
                                <div key={cal.id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50 gap-2">
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        <div className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: cal.color }} />
                                        <div className="min-w-0">
                                            <span className="text-sm font-medium block truncate">{cal.name}</span>
                                            {cal.is_system && <span className="text-[10px] text-muted-foreground">System</span>}
                                            {cal.owner_name && !cal.is_own && <span className="text-[10px] text-muted-foreground">von {cal.owner_name}</span>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        {/* Color picker */}
                                        {(cal.is_own || cal.is_system) && (
                                            <div className="flex gap-0.5">
                                                {colors.slice(0, 4).map(c => (
                                                    <button
                                                        key={c}
                                                        onClick={() => handleColorChange(cal.id, c)}
                                                        className={`w-3.5 h-3.5 rounded-full ${cal.color === c ? 'ring-1 ring-offset-1 ring-gray-400' : 'opacity-50 hover:opacity-100'}`}
                                                        style={{ backgroundColor: c }}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        {/* Share toggle */}
                                        {cal.is_own && !cal.is_system && (
                                            <Button
                                                variant="ghost" size="icon" className="h-6 w-6"
                                                onClick={() => handleToggleShare(cal.id, cal.is_shared)}
                                                title={cal.is_shared ? 'Freigabe entfernen' : 'Fuer andere freigeben'}
                                            >
                                                <Share2 className={`w-3 h-3 ${cal.is_shared ? 'text-primary' : 'text-muted-foreground'}`} />
                                            </Button>
                                        )}
                                        {/* Unsubscribe */}
                                        {cal.is_subscribed && !cal.is_own && !cal.is_system && (
                                            <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground" onClick={() => handleUnsubscribe(cal.id)}>
                                                <X className="w-3 h-3" />
                                            </Button>
                                        )}
                                        {/* Delete */}
                                        {cal.is_own && !cal.is_system && (
                                            <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDelete(cal.id)}>
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Task Calendar Toggle */}
                        <div className="flex items-center justify-between p-2.5 rounded-lg border border-dashed">
                            <div className="flex items-center gap-2.5">
                                <div className="w-4 h-4 rounded-sm bg-violet-500" />
                                <span className="text-sm font-medium">Meine Tasks</span>
                            </div>
                            <Switch
                                checked={showTasksInCalendar}
                                onCheckedChange={(v) => {
                                    setShowTasksInCalendar(v);
                                    localStorage.setItem('agencyos_show_tasks_calendar', String(v));
                                }}
                                data-testid="toggle-tasks-calendar"
                            />
                        </div>

                        {/* Other users' task calendars */}
                        <TaskCalendarSubscriptions api={api} />
                    </TabsContent>

                    <TabsContent value="shared" className="space-y-3 mt-3">
                        {sharedCalendars.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-6">Keine verfuegbaren Kalender</p>
                        ) : (
                            <div className="space-y-1.5">
                                {sharedCalendars.map(cal => (
                                    <div key={cal.id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50">
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: cal.color }} />
                                            <div className="min-w-0">
                                                <span className="text-sm font-medium block truncate">{cal.name}</span>
                                                {cal.owner_name && <span className="text-[10px] text-muted-foreground">von {cal.owner_name}</span>}
                                            </div>
                                        </div>
                                        {subscribedIds.includes(cal.id) ? (
                                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleUnsubscribe(cal.id)}>
                                                Abbestellen
                                            </Button>
                                        ) : (
                                            <Button size="sm" className="h-7 text-xs" onClick={() => handleSubscribe(cal.id)} data-testid={`subscribe-${cal.id}`}>
                                                Abonnieren
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};

// Task Calendar Subscriptions - subscribe to other users' task calendars
const TaskCalendarSubscriptions = ({ api }) => {
    const [users, setUsers] = useState([]);
    const [subscribed, setSubscribed] = useState([]);

    useEffect(() => {
        const load = async () => {
            try {
                const [usersRes, subsRes] = await Promise.all([
                    api.get('/calendar/v2/users'),
                    api.get('/calendar/v2/task-calendars/subscriptions')
                ]);
                setUsers(usersRes.data || []);
                setSubscribed(subsRes.data || []);
            } catch (e) {}
        };
        load();
    }, [api]);

    const toggle = async (userId) => {
        try {
            if (subscribed.includes(userId)) {
                await api.post(`/calendar/v2/task-calendars/${userId}/unsubscribe`);
                setSubscribed(prev => prev.filter(id => id !== userId));
            } else {
                await api.post(`/calendar/v2/task-calendars/${userId}/subscribe`);
                setSubscribed(prev => [...prev, userId]);
            }
        } catch (e) {}
    };

    if (users.length <= 1) return null;

    return (
        <div className="space-y-1.5 pt-2 border-t border-dashed">
            <p className="text-[10px] text-muted-foreground uppercase font-medium">Task-Kalender anderer Personen</p>
            {users.map(u => {
                const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username;
                return (
                    <div key={u.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: u.profile_color || '#6366F1' }} />
                            <span className="text-xs font-medium">{name}</span>
                        </div>
                        <Switch
                            checked={subscribed.includes(u.id)}
                            onCheckedChange={() => toggle(u.id)}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default CalendarPage;
